import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { User, FileText, CheckCircle, LogOut, Copy, Plus, X, Upload, AlertTriangle, Briefcase, GraduationCap, MapPin, CreditCard, Users, FolderOpen, AlertCircle, Lock, Edit, Clock, Send, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EmployeeInsufficiencyTab from '@/components/employee/EmployeeInsufficiencyTab';
import EmployeeDocumentsTab from '@/components/employee/EmployeeDocumentsTab';
import Autocomplete from '@/components/ui/autocomplete';
import { SignaturePad } from '@/components/ui/signature-pad';
import { INDIAN_STATES, INDIAN_CITIES, ALL_CITIES, INDIAN_COMPANIES, INDIAN_UNIVERSITIES, INDIAN_COLLEGES, getCitiesByState } from '@/data/indianData';
import { formatName, formatEmail, formatPhone, formatCompanyName, formatAddress, formatPAN, formatPincode, formatDegree, toTitleCase, formatPhoneMax10, formatDateDDMMYYYY, formatDateForDisplay, formatDateForInput, isValidEmail, isValidPhone } from '@/utils/textFormatting';


export default function EmployeePortal({ user, onLogout }) {
  const [detailsTab, setDetailsTab] = useState('basic');
  const [employee, setEmployee] = useState(null);
  const [verificationCase, setVerificationCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [declarationText, setDeclarationText] = useState('');
  const [clientName, setClientName] = useState('');
  const [agencyName, setAgencyName] = useState('JustVerified');
  const [systemInsufficiencies, setSystemInsufficiencies] = useState([]);
  const [manualInsufficiencies, setManualInsufficiencies] = useState([]);
  
  // Form locking state - locked after submission unless insufficiency raised
  const [activeInsufficiencyClearing, setActiveInsufficiencyClearing] = useState(null); // Which insufficiency is being cleared
  const [backendLockedSections, setBackendLockedSections] = useState([]); // Sections locked by QC approval
  const [basicLockedAfterSubmission, setBasicLockedAfterSubmission] = useState(false); // Basic info locked after initial submission
  const [basicEditApproved, setBasicEditApproved] = useState(false); // Edit request approved - can edit basic info
  const [basicEditFields, setBasicEditFields] = useState([]); // Which fields can be edited
  const [pendingEditRequest, setPendingEditRequest] = useState(null); // Pending edit request
  const [showEditRequestDialog, setShowEditRequestDialog] = useState(false); // Show edit request dialog
  const [editRequestReason, setEditRequestReason] = useState('');
  const [partialSubmissionMode, setPartialSubmissionMode] = useState(false); // Process Basic First - form open for non-basic sections
  const [editRequestFields, setEditRequestFields] = useState([]);

  // Basic Details
  const [gender, setGender] = useState('');
  const [alternateContact, setAlternateContact] = useState('');
  const [alternateContactRelation, setAlternateContactRelation] = useState('');
  const [noAlternateContact, setNoAlternateContact] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [isExtractingResume, setIsExtractingResume] = useState(false);
  const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
  const [extractedData, setExtractedData] = useState(null);

  // Present Address
  const [presentAddress, setPresentAddress] = useState({
    complete_address: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    address_proof: null,
    address_proof_reason: ''
  });

  // Permanent Address
  const [permanentAddress, setPermanentAddress] = useState({
    complete_address: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    address_proof: null,
    address_proof_reason: ''
  });
  const [permanentSameAsPresent, setPermanentSameAsPresent] = useState(false);

  // Additional Addresses (beyond Present and Permanent)
  const [additionalAddresses, setAdditionalAddresses] = useState([]);

  // ID Details
  const [panNumber, setPanNumber] = useState('');
  const [aadharNumber, setAadharNumber] = useState('');
  const [panCardFile, setPanCardFile] = useState(null);
  const [panCardUrl, setPanCardUrl] = useState(null);
  const [aadharCardFile, setAadharCardFile] = useState(null);
  const [aadharCardUrl, setAadharCardUrl] = useState(null);

  // Employee Category (from HR/Client)
  const [employeeCategory, setEmployeeCategory] = useState('experienced'); // fresher, experienced, intern, trainee
  
  // Check if employment is required based on category
  const isEmploymentRequired = employeeCategory === 'experienced';
  
  // Suggested gap reasons for employment gaps
  const GAP_REASONS = [
    'Higher Studies',
    'Holiday/Vacation Break',
    'Marriage',
    'Family Responsibilities',
    'Health Issues',
    'Career Break',
    'Job Search',
    'Relocation',
    'Personal Development/Training',
    'Freelancing/Consulting',
    'Starting Own Business',
    'Maternity/Paternity Leave',
    'Travel',
    'Other'
  ];
  
  const [employmentEntries, setEmploymentEntries] = useState([{
    employment_type: 'current',
    company_name: '',
    location_address: '',
    employee_id: '',
    designation: '',
    employed_from: '',
    employed_to: '',
    salary_per_month: '',
    uan_pf_not_available: false,
    uan_pf_number: '',
    can_verify_current: true,
    date_of_relieving: '',
    gap_reason: '',
    gap_reason_other: '',
    rm_name: '',
    rm_contact_number: '',
    rm_email: '',
    rm_designation: '',
    rm_department: '',
    rm_company: '',
    rm2_name: '',
    rm2_contact_number: '',
    rm2_email: '',
    rm2_designation: '',
    rm2_department: '',
    rm2_company: '',
    hr_name: '',
    hr_email: '',
    hr_contact_number: '',
    hr_details_not_available: false,
    relieving_letter: null,
    relieving_letter_url: null,
    relieving_letter_id: null,
    relieving_letter_reason: '',
    latest_payslip: null,
    latest_payslip_url: null,
    latest_payslip_id: null,
    latest_payslip_reason: ''
  }]);

  // Education Entries
  const [educationEntries, setEducationEntries] = useState([{
    education_type: 'highest',
    acknowledged_cert_type: '',  // Mandatory candidate-side ack; NOT wired to backend check-mapping (Mar 2, 2026)
    name_as_per_document: '',
    university_name: '',
    college: '',
    specialization: '',
    course: '',
    year_of_passing: '',
    roll_no: '',
    degree_certificate: null,
    degree_certificate_url: null,
    degree_certificate_id: null,
    degree_certificate_reason: '',
    marks_card: null,
    marks_card_url: null,
    marks_card_id: null,
    marks_card_reason: ''
  }]);

  // References
  const [personalRefs, setPersonalRefs] = useState([
    { name: '', phone: '', relation: '' },
    { name: '', phone: '', relation: '' },
    { name: '', phone: '', relation: '' }
  ]);
  
  const [professionalRefs, setProfessionalRefs] = useState([
    { name: '', phone: '', designation: '', company: '', email: '' },
    { name: '', phone: '', designation: '', company: '', email: '' },
    { name: '', phone: '', designation: '', company: '', email: '' }
  ]);

  const [declarationSigned, setDeclarationSigned] = useState(false);
  const [declarationAgreed, setDeclarationAgreed] = useState(false);
  
  // Track if RM details are mandatory (based on package having Professional Reference check)
  const [isRmDetailsMandatory, setIsRmDetailsMandatory] = useState(false);
  
  // Package checks requirements
  const [packageChecks, setPackageChecks] = useState([]);
  const [packageName, setPackageName] = useState('');
  const [hasEmploymentCheck, setHasEmploymentCheck] = useState(false);
  const [hasPersonalRefCheck, setHasPersonalRefCheck] = useState(false);
  const [hasProfessionalRefCheck, setHasProfessionalRefCheck] = useState(false);
  
  // Declarations for fewer entries than required
  const [employmentDeclaration, setEmploymentDeclaration] = useState(false);
  const [educationDeclaration, setEducationDeclaration] = useState(false);
  const [addressDeclaration, setAddressDeclaration] = useState(false);
  
  // Signature canvas state
  const signatureCanvasRef = useRef(null);
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  
  // Database suggestions for company/institution autocomplete
  const [dbCompanySuggestions, setDbCompanySuggestions] = useState([]);
  const [dbInstitutionSuggestions, setDbInstitutionSuggestions] = useState([]);
  
  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    basic: {},
    address: {},
    employment: [],
    education: [],
    idDetails: {},
    personalRef: [],
    professionalRef: []
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    fetchEmployeeData();
    fetchDeclarationText();
  }, []);

  // Re-run insufficiency detection when address state changes
  useEffect(() => {
    if (employee) {
      detectSystemInsufficiencies(employee);
    }
  }, [presentAddress, permanentAddress, employee]);

  // ========================================================================
  // LOCAL DRAFT AUTOSAVE (May 7, 2026 — guard against network/save failures)
  // Snapshots form state to localStorage every 30 s and on tab close.
  // On mount, if the case is still in draft state and a snapshot exists
  // newer than 1 hour, restore it silently and toast a one-tap Undo.
  // Snapshot is cleared after a successful declaration submission.
  // ========================================================================
  const draftKey = employee?.id ? `bgv:draft:${employee.id}` : null;
  const skipRestoreOnceRef = useRef(false);
  const draftRestoredRef = useRef(false);

  // Snapshot current form state every 30 s
  useEffect(() => {
    if (!draftKey || declarationSigned) return;
    const snapshot = () => {
      try {
        const payload = {
          v: 1,
          ts: Date.now(),
          gender, alternateContact, alternateContactRelation, noAlternateContact,
          presentAddress, permanentAddress, permanentSameAsPresent, additionalAddresses,
          panNumber, aadharNumber, panCardUrl, aadharCardUrl,
          employeeCategory,
          employmentEntries, educationEntries,
          personalRefs, professionalRefs,
          employmentDeclaration, educationDeclaration, addressDeclaration,
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
      } catch { /* quota / disabled */ }
    };
    const t = setInterval(snapshot, 30000);
    window.addEventListener('beforeunload', snapshot);
    return () => {
      clearInterval(t);
      window.removeEventListener('beforeunload', snapshot);
    };
  }, [
    draftKey, declarationSigned,
    gender, alternateContact, alternateContactRelation, noAlternateContact,
    presentAddress, permanentAddress, permanentSameAsPresent, additionalAddresses,
    panNumber, aadharNumber, panCardUrl, aadharCardUrl, employeeCategory,
    employmentEntries, educationEntries, personalRefs, professionalRefs,
    employmentDeclaration, educationDeclaration, addressDeclaration,
  ]);

  // Restore snapshot once after employee data has loaded (case still in draft)
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current || declarationSigned) return;
    if (skipRestoreOnceRef.current) {
      skipRestoreOnceRef.current = false;
      draftRestoredRef.current = true;
      return;
    }
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) { draftRestoredRef.current = true; return; }
      const snap = JSON.parse(raw);
      if (!snap || snap.v !== 1 || !snap.ts) { draftRestoredRef.current = true; return; }
      const ageMs = Date.now() - snap.ts;
      if (ageMs > 60 * 60 * 1000) {  // older than 1 h → ignore
        localStorage.removeItem(draftKey);
        draftRestoredRef.current = true;
        return;
      }
      // Capture current server-restored values so the user can undo
      const before = {
        gender, alternateContact, alternateContactRelation, noAlternateContact,
        presentAddress, permanentAddress, permanentSameAsPresent, additionalAddresses,
        panNumber, aadharNumber, panCardUrl, aadharCardUrl,
        employeeCategory,
        employmentEntries, educationEntries, personalRefs, professionalRefs,
        employmentDeclaration, educationDeclaration, addressDeclaration,
      };
      // Apply snapshot
      if (snap.gender !== undefined) setGender(snap.gender || '');
      if (snap.alternateContact !== undefined) setAlternateContact(snap.alternateContact || '');
      if (snap.alternateContactRelation !== undefined) setAlternateContactRelation(snap.alternateContactRelation || '');
      if (snap.noAlternateContact !== undefined) setNoAlternateContact(!!snap.noAlternateContact);
      if (snap.presentAddress) setPresentAddress(snap.presentAddress);
      if (snap.permanentAddress) setPermanentAddress(snap.permanentAddress);
      if (snap.permanentSameAsPresent !== undefined) setPermanentSameAsPresent(!!snap.permanentSameAsPresent);
      if (Array.isArray(snap.additionalAddresses)) setAdditionalAddresses(snap.additionalAddresses);
      if (snap.panNumber !== undefined) setPanNumber(snap.panNumber || '');
      if (snap.aadharNumber !== undefined) setAadharNumber(snap.aadharNumber || '');
      if (snap.panCardUrl !== undefined) setPanCardUrl(snap.panCardUrl || '');
      if (snap.aadharCardUrl !== undefined) setAadharCardUrl(snap.aadharCardUrl || '');
      if (snap.employeeCategory !== undefined) setEmployeeCategory(snap.employeeCategory || 'experienced');
      if (Array.isArray(snap.employmentEntries) && snap.employmentEntries.length) setEmploymentEntries(snap.employmentEntries);
      if (Array.isArray(snap.educationEntries) && snap.educationEntries.length) setEducationEntries(snap.educationEntries);
      if (Array.isArray(snap.personalRefs) && snap.personalRefs.length) setPersonalRefs(snap.personalRefs);
      if (Array.isArray(snap.professionalRefs) && snap.professionalRefs.length) setProfessionalRefs(snap.professionalRefs);
      if (snap.employmentDeclaration !== undefined) setEmploymentDeclaration(!!snap.employmentDeclaration);
      if (snap.educationDeclaration !== undefined) setEducationDeclaration(!!snap.educationDeclaration);
      if (snap.addressDeclaration !== undefined) setAddressDeclaration(!!snap.addressDeclaration);

      draftRestoredRef.current = true;
      const mins = Math.max(1, Math.round(ageMs / 60000));
      toast.success(
        `Restored unsaved draft from ${mins} min ago.`,
        {
          duration: 10000,
          action: {
            label: 'Undo',
            onClick: () => {
              skipRestoreOnceRef.current = true;
              setGender(before.gender);
              setAlternateContact(before.alternateContact);
              setAlternateContactRelation(before.alternateContactRelation);
              setNoAlternateContact(before.noAlternateContact);
              setPresentAddress(before.presentAddress);
              setPermanentAddress(before.permanentAddress);
              setPermanentSameAsPresent(before.permanentSameAsPresent);
              setAdditionalAddresses(before.additionalAddresses);
              setPanNumber(before.panNumber);
              setAadharNumber(before.aadharNumber);
              setPanCardUrl(before.panCardUrl);
              setAadharCardUrl(before.aadharCardUrl);
              setEmployeeCategory(before.employeeCategory);
              setEmploymentEntries(before.employmentEntries);
              setEducationEntries(before.educationEntries);
              setPersonalRefs(before.personalRefs);
              setProfessionalRefs(before.professionalRefs);
              setEmploymentDeclaration(before.employmentDeclaration);
              setEducationDeclaration(before.educationDeclaration);
              setAddressDeclaration(before.addressDeclaration);
              try { localStorage.removeItem(draftKey); } catch { /* noop */ }
              toast.info('Draft discarded — showing last saved data from server.');
            }
          }
        }
      );
    } catch {
      draftRestoredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, declarationSigned]);

  // Clear snapshot after successful submission
  useEffect(() => {
    if (declarationSigned && draftKey) {
      try { localStorage.removeItem(draftKey); } catch { /* noop */ }
    }
  }, [declarationSigned, draftKey]);

  const fetchDeclarationText = async () => {
    try {
      const response = await axios.get('/declaration-text');
      setDeclarationText(response.data.declaration_text);
      if (response.data.entity_name) setClientName(response.data.entity_name);
    } catch (error) {
      console.error('Failed to fetch declaration');
    }
  };

  // Fetch database suggestions for company/institution autocomplete
  const fetchDatabaseSuggestions = async (query, type) => {
    if (!query || query.length < 2) return;
    
    try {
      const response = await axios.get(`/database/search-suggestions?query=${encodeURIComponent(query)}&type=${type}`);
      if (type === 'company') {
        const dbSuggestions = (response.data.companies || []).map(c => c.company_name);
        // Combine with static suggestions
        const combined = [...new Set([...dbSuggestions, ...INDIAN_COMPANIES.filter(c => c.toLowerCase().includes(query.toLowerCase()))])];
        setDbCompanySuggestions(combined.slice(0, 15));
      } else if (type === 'institution') {
        const dbSuggestions = (response.data.institutions || []).map(i => i.institution_name || i.university_name);
        // Combine with static suggestions
        const combined = [...new Set([...dbSuggestions, ...INDIAN_UNIVERSITIES.filter(u => u.toLowerCase().includes(query.toLowerCase()))])];
        setDbInstitutionSuggestions(combined.slice(0, 15));
      }
    } catch (error) {
      console.error('Failed to fetch database suggestions:', error);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      const empResponse = await axios.get('/employees');
      if (empResponse.data.length > 0) {
        const empData = empResponse.data[0];
        setEmployee(empData);
        setDeclarationSigned(empData.declaration_signed);

        // Populate Basic Details
        if (empData.gender) setGender(empData.gender);
        if (empData.alternate_contact) setAlternateContact(empData.alternate_contact);
        if (empData.alternate_contact_relation) setAlternateContactRelation(empData.alternate_contact_relation);
        if (empData.no_alternate_contact) setNoAlternateContact(empData.no_alternate_contact);
        
        // Populate Address Details
        if (empData.present_address) {
          setPresentAddress({
            complete_address: empData.present_address.complete_address || '',
            landmark: empData.present_address.landmark || '',
            city: empData.present_address.city || '',
            state: empData.present_address.state || '',
            pincode: empData.present_address.pincode || '',
            address_proof: null,
            address_proof_url: empData.present_address.address_proof_url || null,
            address_proof_reason: empData.present_address.address_proof_reason || ''
          });
        }
        if (empData.permanent_address) {
          setPermanentAddress({
            complete_address: empData.permanent_address.complete_address || '',
            landmark: empData.permanent_address.landmark || '',
            city: empData.permanent_address.city || '',
            state: empData.permanent_address.state || '',
            pincode: empData.permanent_address.pincode || '',
            address_proof: null,
            address_proof_url: empData.permanent_address.address_proof_url || null,
            address_proof_reason: empData.permanent_address.address_proof_reason || ''
          });
        }
        if (empData.permanent_same_as_present) {
          setPermanentSameAsPresent(true);
        }
        
        // Populate Additional Addresses
        if (empData.additional_addresses && empData.additional_addresses.length > 0) {
          setAdditionalAddresses(empData.additional_addresses.map(addr => ({
            address_type: addr.address_type || 'previous',
            complete_address: addr.complete_address || '',
            landmark: addr.landmark || '',
            city: addr.city || '',
            state: addr.state || '',
            pincode: addr.pincode || '',
            duration_from: addr.duration_from || '',
            duration_to: addr.duration_to || ''
          })));
        }
        
        // Populate ID Details
        if (empData.pan_number) setPanNumber(empData.pan_number);
        if (empData.aadhar_number) setAadharNumber(empData.aadhar_number);
        if (empData.pan_card_url) setPanCardUrl(empData.pan_card_url);
        if (empData.aadhar_card_url) setAadharCardUrl(empData.aadhar_card_url);
        
        // Populate Employee Category (from HR/Client)
        if (empData.employee_category) {
          setEmployeeCategory(empData.employee_category);
        } else if (empData.is_fresher) {
          // Backward compatibility: convert old is_fresher to category
          setEmployeeCategory('fresher');
        }
        
        // Populate Employment Entries
        if (empData.employment_entries && empData.employment_entries.length > 0) {
          setEmploymentEntries(empData.employment_entries.map(emp => ({
            employment_type: emp.employment_type || 'current',
            company_name: emp.company_name || '',
            location_address: emp.location_address || '',
            employee_id: emp.employee_id || '',
            designation: emp.designation || '',
            employed_from: emp.employed_from || '',
            employed_to: emp.employed_to || '',
            salary_per_month: emp.salary_per_month || '',
            uan_pf_not_available: emp.uan_pf_not_available || false,
            uan_pf_number: emp.uan_pf_number || '',
            can_verify_current: emp.can_verify_current !== false,
            date_of_relieving: emp.date_of_relieving || '',
            gap_reason: emp.gap_reason || '',
            gap_reason_other: emp.gap_reason_other || '',
            rm_name: emp.rm_name || '',
            rm_contact_number: emp.rm_contact_number || '',
            rm_email: emp.rm_email || '',
            rm_designation: emp.rm_designation || '',
            rm_department: emp.rm_department || '',
            rm_company: emp.rm_company || '',
            rm2_name: emp.rm2_name || '',
            rm2_contact_number: emp.rm2_contact_number || '',
            rm2_email: emp.rm2_email || '',
            rm2_designation: emp.rm2_designation || '',
            rm2_department: emp.rm2_department || '',
            rm2_company: emp.rm2_company || '',
            hr_name: emp.hr_name || '',
            hr_email: emp.hr_email || '',
            hr_contact_number: emp.hr_contact_number || '',
            hr_details_not_available: emp.hr_details_not_available || false,
            relieving_letter: null,
            relieving_letter_url: emp.relieving_letter_url || null,
            relieving_letter_id: emp.relieving_letter_id || null,
            relieving_letter_reason: emp.relieving_letter_reason || '',
            latest_payslip: null,
            latest_payslip_url: emp.latest_payslip_url || null,
            latest_payslip_id: emp.latest_payslip_id || null,
            latest_payslip_reason: emp.latest_payslip_reason || ''
          })));
        }
        
        // Populate Education Entries
        if (empData.education_entries && empData.education_entries.length > 0) {
          setEducationEntries(empData.education_entries.map(edu => ({
            education_type: edu.education_type || 'highest',
            name_as_per_document: edu.name_as_per_document || '',
            university_name: edu.university_name || '',
            college: edu.college || '',
            specialization: edu.specialization || '',
            course: edu.course || '',
            year_of_passing: edu.year_of_passing || '',
            roll_no: edu.roll_no || '',
            degree_certificate: null,
            degree_certificate_url: edu.degree_certificate_url || null,
            degree_certificate_id: edu.degree_certificate_id || null,
            degree_certificate_reason: edu.degree_certificate_reason || '',
            marks_card: null,
            marks_card_url: edu.marks_card_url || null,
            marks_card_id: edu.marks_card_id || null,
            marks_card_reason: edu.marks_card_reason || ''
          })));
        }
        
        // Populate Personal References
        if (empData.personal_references && empData.personal_references.length > 0) {
          const loadedPersonalRefs = empData.personal_references.map(ref => ({
            name: ref.name || '',
            phone: ref.phone || '',
            relation: ref.relation || ''
          }));
          // Pad with empty entries if less than 3
          while (loadedPersonalRefs.length < 3) {
            loadedPersonalRefs.push({ name: '', phone: '', relation: '' });
          }
          setPersonalRefs(loadedPersonalRefs);
        }
        
        // Populate Professional References
        if (empData.professional_references && empData.professional_references.length > 0) {
          const loadedProfRefs = empData.professional_references.map(ref => ({
            name: ref.name || '',
            phone: ref.phone || '',
            designation: ref.designation || '',
            company: ref.company || '',
            email: ref.email || ''
          }));
          // Pad with empty entries if less than 3
          while (loadedProfRefs.length < 3) {
            loadedProfRefs.push({ name: '', phone: '', designation: '', company: '', email: '' });
          }
          setProfessionalRefs(loadedProfRefs);
        }

        // Fetch client name and package info
        if (empData.entity_id) {
          // Fetch entity directly by ID to get package_id
          try {
            const entityResponse = await axios.get(`/entities/${empData.entity_id}`);
            const entity = entityResponse.data;
            if (entity) {
              const clientsResponse = await axios.get('/clients');
              const client = clientsResponse.data.find(c => c.id === entity.client_id);
              if (client) {
                setClientName(client.company_name);
              }
              
              // Fetch package to check if Professional Reference check is required
              if (entity.package_id) {
                try {
                  const packageResponse = await axios.get(`/packages/${entity.package_id}`);
                  const pkg = packageResponse.data;
                  if (pkg && pkg.checks) {
                    // Check if package includes specific checks
                    const checkTypes = pkg.checks.map(check => check.check_type || check);
                    
                    const hasProfRefCheck = checkTypes.includes('professional_reference');
                    // reference_check is treated as personal_reference (generic reference check)
                    const hasPersonalRef = checkTypes.includes('personal_reference') || checkTypes.includes('reference_check');
                    const hasEmpCheck = checkTypes.includes('employment_check');
                    
                    // Get reference quantity for setting up the correct number of rows
                    const refCheck = pkg.checks.find(c => c.check_type === 'reference_check' || c.check_type === 'personal_reference');
                    if (refCheck && refCheck.quantity) {
                      // Ensure we have enough personal reference rows
                      const neededCount = refCheck.quantity;
                      setPersonalRefs(prev => {
                        if (prev.length < neededCount) {
                          const additional = Array(neededCount - prev.length).fill().map(() => ({ name: '', phone: '', relation: '' }));
                          return [...prev, ...additional];
                        }
                        return prev;
                      });
                    }
                    
                    setIsRmDetailsMandatory(hasProfRefCheck);
                    setHasProfessionalRefCheck(hasProfRefCheck);
                    setHasPersonalRefCheck(hasPersonalRef);
                    setHasEmploymentCheck(hasEmpCheck);
                    
                    // Store package checks for Review & Submit
                    setPackageChecks(pkg.checks || []);
                    setPackageName(pkg.name || '');
                  }
                } catch (pkgError) {
                  console.error('Failed to fetch package:', pkgError);
                }
              }
            }
          } catch (entityError) {
            console.error('Failed to fetch entity:', entityError);
          }
        }

        // Load declarations from employee data
        if (empData.employment_declaration !== undefined) {
          setEmploymentDeclaration(empData.employment_declaration);
        }
        if (empData.education_declaration !== undefined) {
          setEducationDeclaration(empData.education_declaration);
        }
        if (empData.address_declaration !== undefined) {
          setAddressDeclaration(empData.address_declaration);
        }

        // Detect system insufficiencies
        detectSystemInsufficiencies(empData);
        
        // Load manual insufficiencies
        try {
          const insuffResponse = await axios.get('/employee/insufficiencies');
          setManualInsufficiencies(insuffResponse.data || []);
        } catch (e) {
          console.log('No manual insufficiencies');
        }

        if (empData.case_id) {
          const caseResponse = await axios.get(`/cases/${empData.case_id}`);
          setVerificationCase(caseResponse.data);
          
          // Fetch locked checks/sections from backend
          try {
            const lockedResponse = await axios.get('/employee/locked-checks');
            setBackendLockedSections(lockedResponse.data.locked_sections || []);
            setBasicLockedAfterSubmission(lockedResponse.data.basic_locked_after_submission || false);
            setBasicEditApproved(lockedResponse.data.basic_edit_approved || false);
            setBasicEditFields(lockedResponse.data.basic_edit_fields || []);
            setPartialSubmissionMode(lockedResponse.data.partial_submission_mode || false);
          } catch (e) {
            console.log('Could not fetch locked checks');
          }
          
          // Fetch pending edit request
          try {
            const editRequestResponse = await axios.get('/employee/basic-edit-request');
            setPendingEditRequest(editRequestResponse.data.pending_request);
          } catch (e) {
            console.log('Could not fetch edit request');
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  // Detect missing mandatory fields
  const detectSystemInsufficiencies = (emp) => {
    if (!emp) return;
    
    const detected = [];
    
    // Address Proof checks — skip entirely if Address section is locked by
    // backend (bulk-imported by QC/Client). Address Proof is not mandatory
    // for the Address Check, so candidates shouldn't see insufficiency
    // prompts on sections they can't edit.
    const isAddressLocked = backendLockedSections.includes('address');

    // Check if any present address field has data
    const hasPresentAddressData = presentAddress.complete_address || presentAddress.city || presentAddress.state || presentAddress.pincode;
    
    // Present Address Proof - Required if any address data exists (check both file and URL)
    if (!isAddressLocked && hasPresentAddressData && !presentAddress.address_proof && !presentAddress.address_proof_url && presentAddress.address_proof_reason !== 'not_available') {
      detected.push({
        id: 'sys_present_address_proof',
        type: 'system',
        check_type: 'address_check',
        field: 'present_address_proof',
        reason: 'Present Address Proof Missing',
        description: 'Please upload address proof for your Present Address (Utility Bill, Bank Statement, Rent Agreement, etc.)',
        entry_type: 'address'
      });
    }
    
    // Check if any permanent address field has data
    const hasPermanentAddressData = permanentAddress.complete_address || permanentAddress.city || permanentAddress.state || permanentAddress.pincode;
    
    // Permanent Address Proof - Required if any address data exists (check both file and URL)
    if (!isAddressLocked && hasPermanentAddressData && !permanentAddress.address_proof && !permanentAddress.address_proof_url && permanentAddress.address_proof_reason !== 'not_available') {
      detected.push({
        id: 'sys_permanent_address_proof',
        type: 'system',
        check_type: 'address_check',
        field: 'permanent_address_proof',
        reason: 'Permanent Address Proof Missing',
        description: 'Please upload address proof for your Permanent Address (Utility Bill, Bank Statement, Rent Agreement, etc.)',
        entry_type: 'address'
      });
    }
    
    // Education entries check
    if (emp.education_entries && emp.education_entries.length > 0) {
      emp.education_entries.forEach((edu, index) => {
        // Roll Number mandatory
        if (!edu.roll_no && !edu.roll_number) {
          detected.push({
            id: `sys_edu_roll_${index}`,
            type: 'system',
            check_type: 'education_check',
            field: 'roll_number',
            reason: 'Roll Number Missing',
            description: `Roll Number is required for ${edu.course || edu.degree || 'Education'} at ${edu.university_name || edu.college || 'Institution'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'education'
          });
        }
      });
    }
    
    // Employment entries check
    if (emp.employment_entries && emp.employment_entries.length > 0) {
      emp.employment_entries.forEach((job, index) => {
        // Employee Code mandatory
        if (!job.employee_code && !job.employee_id) {
          detected.push({
            id: `sys_emp_code_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'employee_code',
            reason: 'Employee Code Missing',
            description: `Employee Code/ID is required for ${job.company_name || 'Company'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // Salary mandatory
        if (!job.salary && !job.salary_per_month && !job.salary_per_annum && !job.ctc) {
          detected.push({
            id: `sys_emp_salary_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'salary',
            reason: 'Salary Information Missing',
            description: `Salary (Per Month/Per Annum) is required for ${job.company_name || 'Company'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // UAN/PF Number - only if marked as available
        // UAN/PF Number - Mandatory unless "Not Available" is checked
        if (!job.uan_pf_not_available && !job.uan_pf_number) {
          detected.push({
            id: `sys_emp_uan_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'uan_pf_number',
            reason: 'UAN/PF Number Missing',
            description: `UAN/PF Number is required for ${job.company_name || 'Company'} (Entry ${index + 1}). Check "UAN/PF Number Not Available" if not applicable.`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // Current employment - Date of Relieving check
        // Only applies to Current employment type
        if (job.employment_type === 'current' && job.can_verify_current === false && !job.date_of_relieving) {
          detected.push({
            id: `sys_emp_relieving_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'date_of_relieving',
            reason: 'Date of Relieving Required',
            description: `Employee still active at ${job.company_name || 'Company'}. Since verification from current employer is not permitted, Date of Relieving is required. (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment',
            remark: 'Employee still active'
          });
        }
        
        // Employment Gap check - check gap between this entry and the NEXT one (chronologically older)
        if (index < emp.employment_entries.length - 1) {
          const nextJob = emp.employment_entries[index + 1]; // Next entry is chronologically older
          const currentStart = job.employed_from;
          const nextEnd = nextJob.employed_to || nextJob.date_of_relieving;
          
          if (currentStart && nextEnd) {
            const startDate = new Date(currentStart);
            const endDate = new Date(nextEnd);
            const gapDays = Math.ceil((startDate - endDate) / (1000 * 60 * 60 * 24));
            
            if (gapDays > 30) {
              // Check if gap reason is provided
              const hasGapReason = job.gap_reason && (job.gap_reason !== 'Other' || job.gap_reason_other);
              if (!hasGapReason) {
                detected.push({
                  id: `sys_emp_gap_${index}`,
                  type: 'system',
                  check_type: 'employment_check',
                  field: 'gap_reason',
                  reason: 'Employment Gap Explanation Required',
                  description: `There is a ${gapDays}-day gap between ${nextJob.company_name || 'Previous Employment'} and ${job.company_name || 'this employment'}. Please provide a reason for this gap. (Entry ${index + 1})`,
                  entry_index: index,
                  entry_type: 'employment',
                  gap_days: gapDays
                });
              }
            }
          }
        }
      });
    }
    
    setSystemInsufficiencies(detected);
  };

  // Check if there are any open insufficiencies
  const hasOpenInsufficiencies = () => {
    const openManual = manualInsufficiencies.filter(i => i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed');
    return systemInsufficiencies.length > 0 || openManual.length > 0;
  };

  const getTotalInsufficiencyCount = () => {
    const openManual = manualInsufficiencies.filter(i => i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed');
    return systemInsufficiencies.length + openManual.length;
  };

  // ============ FORM LOCKING LOGIC ============
  // Check if form is locked (submitted and not in draft status)
  const isFormLocked = () => {
    if (!verificationCase) return false;
    
    // PARTIAL SUBMISSION MODE: "Process Basic Verification First" workflow
    // Basic checks are promoted to QC but employee hasn't fully submitted yet
    // Form stays OPEN so employee can fill remaining sections (employment, education, etc.)
    if (partialSubmissionMode) return false;
    
    // Form is locked ONLY after employee has actually submitted their details
    // AND status is in the verification pipeline
    const lockedStatuses = ['pending_qc', 'in_progress', 'completed', 'assigned', 'qc_approved', 'final_report_qc', 'report_approved'];
    
    // Check if actually submitted by the employee or data entry team
    const isSubmitted = verificationCase.employee_submitted || 
                        (employee?.details_filled && employee?.declaration_signed);
    
    // Form is locked only if submitted AND in locked status
    return isSubmitted && lockedStatuses.includes(verificationCase.status);
  };

  // Get open insufficiencies for a specific check type/section
  const getOpenInsufficienciesForSection = (section) => {
    const sectionToCheckTypeMap = {
      'basic': ['id_check', 'personal_info'],
      'address': ['address_check'],
      'employment': ['employment_check'],
      'education': ['education_check'],
      'id': ['id_check'],
      'references': ['personal_reference', 'professional_reference', 'prof_ref', 'personal_ref', 'reference_check']
    };
    
    const checkTypes = sectionToCheckTypeMap[section] || [];
    
    // Check manual insufficiencies
    const openManual = manualInsufficiencies.filter(i => 
      (i.status !== 'cleared' && i.status !== 'resolved') &&
      (checkTypes.includes(i.check_type) || checkTypes.some(ct => i.check_type?.includes(ct)))
    );
    
    // Check system insufficiencies - these are based on section
    const sectionInsuff = systemInsufficiencies.filter(i => 
      i.section === section || checkTypes.some(ct => i.check_type?.includes(ct))
    );
    
    return [...openManual, ...sectionInsuff];
  };

  // Check if a specific section can be edited
  const canEditSection = (section) => {
    // If form is not locked by case status, check backend-locked sections
    if (!isFormLocked()) {
      // Even if case status doesn't lock, backend might lock specific sections after QC approval
      if (backendLockedSections.includes(section)) {
        // Section is locked by QC approval, but allow if there's an open insufficiency
        const openInsuffs = getOpenInsufficienciesForSection(section);
        return openInsuffs.length > 0;
      }
      return true;
    }
    
    // If user is actively clearing an insufficiency for this section
    if (activeInsufficiencyClearing === section) return true;
    
    // Check if there are open insufficiencies for this section
    const openInsuffs = getOpenInsufficienciesForSection(section);
    return openInsuffs.length > 0;
  };
  
  // Check if a section is locked by backend (QC approved this check type)
  const isSectionLockedByBackend = (section) => {
    return backendLockedSections.includes(section);
  };

  // Start clearing insufficiency for a section
  const startClearingInsufficiency = (section) => {
    setActiveInsufficiencyClearing(section);
    // Switch to the appropriate tab
    setDetailsTab(section);
    toast.info(`You can now edit the ${section} section to clear the insufficiency`);
  };

  // Cancel clearing insufficiency
  const cancelClearingInsufficiency = () => {
    setActiveInsufficiencyClearing(null);
  };

  // ============ BASIC INFO EDIT REQUEST FUNCTIONS ============
  const EDITABLE_BASIC_FIELDS = [
    { id: 'name', label: 'Full Name' },
    { id: 'father_name', label: "Father's Name" },
    { id: 'dob', label: 'Date of Birth' },
    { id: 'phone', label: 'Phone Number' },
    { id: 'alternate_contact', label: 'Alternate Contact' },
    { id: 'gender', label: 'Gender' }
  ];

  const handleSubmitEditRequest = async () => {
    if (editRequestFields.length === 0) {
      toast.error('Please select at least one field to edit');
      return;
    }
    if (!editRequestReason.trim()) {
      toast.error('Please provide a reason for the edit request');
      return;
    }

    try {
      const response = await axios.post('/employee/request-basic-edit', {
        fields_to_edit: editRequestFields,
        reason: editRequestReason
      });
      
      toast.success('Edit request submitted successfully!');
      setShowEditRequestDialog(false);
      setEditRequestFields([]);
      setEditRequestReason('');
      
      // Refresh the pending request
      const editRequestResponse = await axios.get('/employee/basic-edit-request');
      setPendingEditRequest(editRequestResponse.data.pending_request);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit edit request');
    }
  };

  const handleCompleteEdit = async () => {
    try {
      await axios.post('/employee/complete-basic-edit');
      toast.success('Edit completed. Your basic information is now locked again.');
      
      // Refresh locked checks
      const lockedResponse = await axios.get('/employee/locked-checks');
      setBackendLockedSections(lockedResponse.data.locked_sections || []);
      setBasicLockedAfterSubmission(lockedResponse.data.basic_locked_after_submission || false);
      setBasicEditApproved(false);
      setBasicEditFields([]);
      
      // Clear pending request
      const editRequestResponse = await axios.get('/employee/basic-edit-request');
      setPendingEditRequest(editRequestResponse.data.pending_request);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete edit');
    }
  };

  const toggleEditField = (fieldId) => {
    setEditRequestFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };
  // ============ END BASIC INFO EDIT REQUEST FUNCTIONS ============

  // ============ END FORM LOCKING LOGIC ============

  const handleCopyAddress = () => {
    setPermanentAddress({ ...presentAddress });
    setPermanentSameAsPresent(true);
    toast.success('Address copied to permanent address');
  };

  // Additional Address Functions
  const addAdditionalAddress = () => {
    setAdditionalAddresses([...additionalAddresses, {
      address_type: 'previous',
      complete_address: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      duration_from: '',
      duration_to: ''
    }]);
  };

  const removeAdditionalAddress = (index) => {
    setAdditionalAddresses(additionalAddresses.filter((_, i) => i !== index));
  };

  const updateAdditionalAddress = (index, field, value) => {
    const updated = [...additionalAddresses];
    updated[index][field] = value;
    setAdditionalAddresses(updated);
  };

  // Helper: compute total required count for a given check type using
  // packageChecks (authoritative source, summed by quantity). Falls back to
  // counting entries in verificationCase.checks when packageChecks is empty
  // (e.g. package not loaded yet or custom checks attached directly to the
  // case). This mirrors the logic used on Review & Submit so the Add button
  // counter stays consistent with the "Required: N | Filled: M" banner.
  const getRequiredCountForCheckType = (checkType) => {
    if (packageChecks && packageChecks.length > 0) {
      return packageChecks.reduce((sum, c) => {
        const t = typeof c === 'string' ? c : (c?.check_type || c?.type);
        const qty = typeof c === 'object' && c !== null ? (c.quantity || 1) : 1;
        return t === checkType ? sum + qty : sum;
      }, 0);
    }
    return (verificationCase?.checks || []).filter(c => c.check_type === checkType).length;
  };

  // Employment Functions
  const addEmploymentEntry = () => {
    // Check if employee has reached the required count from package
    const requiredEmpChecks = getRequiredCountForCheckType('employment_check');
    // If package defines employment checks, use that count as limit
    // If no employment checks in package, don't allow adding more than current count (prevent new additions)
    if (requiredEmpChecks > 0 && employmentEntries.length >= requiredEmpChecks) {
      toast.error(`All required Employment details (${requiredEmpChecks}) as per client requirement are filled. Please continue to review and submit.`);
      return;
    }
    setEmploymentEntries([...employmentEntries, {
      employment_type: 'past',
      company_name: '',
      location_address: '',
      employee_id: '',
      designation: '',
      employed_from: '',
      employed_to: '',
      salary_per_month: '',
      uan_pf_not_available: false,
      uan_pf_number: '',
      can_verify_current: true,
      date_of_relieving: '',
      gap_reason: '',
      gap_reason_other: '',
      rm_name: '',
      rm_contact_number: '',
      rm_email: '',
      rm_designation: '',
      rm_department: '',
      rm_company: '',
      rm2_name: '',
      rm2_contact_number: '',
      rm2_email: '',
      rm2_designation: '',
      rm2_department: '',
      rm2_company: '',
      hr_name: '',
      hr_email: '',
      hr_contact_number: '',
      relieving_letter: null,
      relieving_letter_url: null,
      relieving_letter_id: null,
      relieving_letter_reason: '',
      latest_payslip: null,
      latest_payslip_url: null,
      latest_payslip_id: null,
      latest_payslip_reason: ''
    }]);
  };
  
  // Calculate gap in days between two dates
  const calculateGapDays = (endDate, startDate) => {
    if (!endDate || !startDate) return 0;
    const end = new Date(endDate);
    const start = new Date(startDate);
    const diffTime = start - end;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Check if there's a gap before this employment entry
  // Gap is between this entry's start date and the NEXT entry's (chronologically older) end date
  const getGapBeforeEntry = (index) => {
    if (index >= employmentEntries.length - 1) return null; // No gap check for last entry (oldest job)
    
    const currentEntry = employmentEntries[index];
    const nextEntry = employmentEntries[index + 1]; // Next entry is chronologically older
    
    // This entry's start date and next (older) entry's end date
    const currentStart = currentEntry.employed_from;
    const nextEnd = nextEntry.employed_to || nextEntry.date_of_relieving;
    
    if (!currentStart || !nextEnd) return null;
    
    const gapDays = calculateGapDays(nextEnd, currentStart);
    
    if (gapDays > 30) {
      return {
        days: gapDays,
        fromDate: nextEnd,
        toDate: currentStart,
        previousCompany: nextEntry.company_name || 'Previous Employment'
      };
    }
    
    return null;
  };

  // Copy RM details to Professional References (both RM1 and RM2)
  const copyRMToReferences = (empIndex) => {
    const emp = employmentEntries[empIndex];
    const newRefs = [...professionalRefs];
    let copiedCount = 0;
    
    // Copy RM1 if available
    if (emp.rm_name || emp.rm_contact_number) {
      const emptyIndex = newRefs.findIndex(ref => !ref.name && !ref.phone);
      const targetIndex = emptyIndex >= 0 ? emptyIndex : 0;
      
      newRefs[targetIndex] = {
        name: emp.rm_name || '',
        phone: emp.rm_contact_number || '',
        designation: emp.rm_designation || '',
        company: emp.rm_company || emp.company_name || '',
        email: emp.rm_email || ''
      };
      copiedCount++;
    }
    
    // Copy RM2 if available
    if (emp.rm2_name || emp.rm2_contact_number) {
      const emptyIndex = newRefs.findIndex(ref => !ref.name && !ref.phone);
      const targetIndex = emptyIndex >= 0 ? emptyIndex : Math.min(1, newRefs.length - 1);
      
      newRefs[targetIndex] = {
        name: emp.rm2_name || '',
        phone: emp.rm2_contact_number || '',
        designation: emp.rm2_designation || '',
        company: emp.rm2_company || emp.company_name || '',
        email: emp.rm2_email || ''
      };
      copiedCount++;
    }
    
    if (copiedCount > 0) {
      setProfessionalRefs(newRefs);
      toast.success(`${copiedCount} Reporting Manager detail(s) copied to Professional References`);
    } else {
      toast.error('Please fill in at least one Reporting Manager details first');
    }
  };

  const removeEmploymentEntry = (index) => {
    setEmploymentEntries(employmentEntries.filter((_, i) => i !== index));
  };

  const updateEmploymentEntry = (index, field, value) => {
    const updated = [...employmentEntries];
    updated[index][field] = value;
    setEmploymentEntries(updated);
  };

  // Education Functions
  const addEducationEntry = () => {
    // Check if employee has reached the required count from package
    const requiredEduChecks = getRequiredCountForCheckType('education_check');
    if (requiredEduChecks > 0 && educationEntries.length >= requiredEduChecks) {
      toast.error(`All required Education details (${requiredEduChecks}) as per client requirement are filled. Please continue to review and submit.`);
      return;
    }
    setEducationEntries([...educationEntries, {
      education_type: 'prior',
      acknowledged_cert_type: '',
      name_as_per_document: '',
      university_name: '',
      college: '',
      specialization: '',
      course: '',
      year_of_passing: '',
      roll_no: '',
      degree_certificate: null,
      degree_certificate_url: null,
      degree_certificate_id: null,
      degree_certificate_reason: '',
      marks_card: null,
      marks_card_url: null,
      marks_card_id: null,
      marks_card_reason: ''
    }]);
  };

  const removeEducationEntry = (index) => {
    setEducationEntries(educationEntries.filter((_, i) => i !== index));
  };

  const updateEducationEntry = (index, field, value) => {
    const updated = [...educationEntries];
    updated[index][field] = value;
    setEducationEntries(updated);
  };

  // Upload a file to the server
  const uploadFileToServer = async (file, documentType, checkType = null) => {
    if (!file) {
      throw new Error('No file provided');
    }
    if (!employee?.id) {
      throw new Error('Employee data not loaded yet. Please wait and try again.');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('employee_id', employee.id);
    formData.append('document_type', documentType);
    if (verificationCase?.id) {
      formData.append('case_id', verificationCase.id);
    }
    if (checkType) {
      formData.append('check_type', checkType);
    }
    
    try {
      const response = await axios.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  };

  const handleFileSelect = async (category, index, field, file) => {
    if (category === 'employment') {
      const updated = [...employmentEntries];
      updated[index][field] = file;
      setEmploymentEntries(updated);
      
      // Upload immediately
      try {
        const result = await uploadFileToServer(file, field, 'employment_check');
        if (result) {
          // Update with URL after successful upload
          const updatedWithUrl = [...employmentEntries];
          updatedWithUrl[index][field] = file;
          updatedWithUrl[index][`${field}_url`] = result.file_url;
          updatedWithUrl[index][`${field}_id`] = result.id;
          setEmploymentEntries(updatedWithUrl);
          toast.success(`${file.name} uploaded successfully`);
        }
      } catch (error) {
        console.error('Employment file upload error:', error);
        toast.error(error.message || `Failed to upload ${file.name}`);
      }
    } else if (category === 'education') {
      const updated = [...educationEntries];
      updated[index][field] = file;
      setEducationEntries(updated);
      
      // Upload immediately
      try {
        const result = await uploadFileToServer(file, field, 'education_check');
        if (result) {
          // Update with URL after successful upload
          const updatedWithUrl = [...educationEntries];
          updatedWithUrl[index][field] = file;
          updatedWithUrl[index][`${field}_url`] = result.file_url;
          updatedWithUrl[index][`${field}_id`] = result.id;
          setEducationEntries(updatedWithUrl);
          toast.success(`${file.name} uploaded successfully`);
        }
      } catch (error) {
        console.error('Education file upload error:', error);
        toast.error(error.message || `Failed to upload ${file.name}`);
      }
    }
  };

  const handlePhotoUpload = async (file) => {
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to server
      try {
        await uploadFileToServer(file, 'passport_photo');
        toast.success('Photo uploaded successfully');
      } catch (error) {
        toast.error('Failed to upload photo');
      }
    }
  };

  const handleResumeUpload = async (file) => {
    if (file) {
      setResumeFile(file);
      try {
        await uploadFileToServer(file, 'resume');
        toast.success('Resume uploaded successfully');
        
        // Ask user if they want to auto-fill from resume
        if (window.confirm('Would you like to auto-fill your details from this resume using AI? This will extract your information automatically.')) {
          await extractAndFillFromResume(file);
        }
      } catch (error) {
        toast.error('Failed to upload resume');
      }
    }
  };

  // Extract data from resume using AI and auto-fill form
  const extractAndFillFromResume = async (file) => {
    setIsExtractingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'resume');
      
      const response = await axios.post('/extract-document-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        setShowAutoFillDialog(true);
      } else {
        toast.error('Could not extract data from resume. Please fill manually.');
      }
    } catch (error) {
      console.error('Resume extraction error:', error);
      toast.error('Failed to extract data from resume');
    } finally {
      setIsExtractingResume(false);
    }
  };

  // Apply extracted data to form fields
  const applyExtractedData = () => {
    if (!extractedData) return;
    
    const { personal_info, employment_history, education, id_documents, references } = extractedData;
    
    // Apply personal info
    if (personal_info) {
      if (personal_info.gender) setGender(personal_info.gender.toLowerCase());
      if (personal_info.alternate_phone) setAlternateContact(formatPhone(personal_info.alternate_phone));
      if (personal_info.father_name) {
        // Update employee father name via API
        axios.put('/employee/profile', { father_name: formatName(personal_info.father_name) }).catch(() => {});
      }
      
      // Apply current/present address
      if (personal_info.current_address) {
        setPresentAddress(prev => ({
          ...prev,
          complete_address: formatAddress(personal_info.current_address.complete_address || ''),
          city: toTitleCase(personal_info.current_address.city || ''),
          state: toTitleCase(personal_info.current_address.state || ''),
          pincode: formatPincode(personal_info.current_address.pincode || '')
        }));
      }
      
      // Apply permanent address
      if (personal_info.permanent_address) {
        setPermanentAddress(prev => ({
          ...prev,
          complete_address: formatAddress(personal_info.permanent_address.complete_address || ''),
          city: toTitleCase(personal_info.permanent_address.city || ''),
          state: toTitleCase(personal_info.permanent_address.state || ''),
          pincode: formatPincode(personal_info.permanent_address.pincode || '')
        }));
      }
    }
    
    // Apply employment history
    if (employment_history && employment_history.length > 0) {
      const formattedEmployment = employment_history.map((emp, idx) => ({
        employment_type: idx === 0 ? 'current' : 'previous',
        company_name: formatCompanyName(emp.company_name || ''),
        designation: toTitleCase(emp.designation || ''),
        employed_from: emp.employed_from || '',
        employed_to: emp.is_current ? '' : (emp.employed_to || ''),
        employee_id: emp.employee_id || '',
        hr_name: formatName(emp.hr_name || ''),
        hr_email: formatEmail(emp.hr_email || ''),
        hr_phone: formatPhone(emp.hr_phone || ''),
        is_current: emp.is_current || idx === 0
      }));
      setEmploymentEntries(formattedEmployment);
    }
    
    // Apply education
    if (education && education.length > 0) {
      const formattedEducation = education.map((edu, idx) => ({
        education_type: idx === 0 ? 'highest' : 'additional',
        course: formatDegree(edu.degree || edu.qualification || ''),
        specialization: toTitleCase(edu.specialization || ''),
        university_name: toTitleCase(edu.university || ''),
        college: toTitleCase(edu.institution || edu.college || ''),
        year_of_passing: edu.year_of_passing || '',
        roll_number: edu.roll_number || '',
        percentage: edu.percentage || ''
      }));
      setEducationEntries(formattedEducation);
    }
    
    // Apply ID documents
    if (id_documents) {
      if (id_documents.pan_number) setPanNumber(formatPAN(id_documents.pan_number));
      if (id_documents.aadhar_number) setAadharNumber(id_documents.aadhar_number);
    }
    
    // Apply references
    if (references) {
      // Professional references
      if (references.length > 0 && references[0].relation === 'professional') {
        const profRefs = references.filter(r => r.relation === 'professional').map(ref => ({
          name: formatName(ref.name || ''),
          phone: formatPhone(ref.phone || ''),
          designation: toTitleCase(ref.designation || ''),
          company: formatCompanyName(ref.company || ''),
          email: formatEmail(ref.email || '')
        }));
        if (profRefs.length > 0) setProfessionalRefs(profRefs);
      }
      
      // Personal references
      const persRefs = references.filter(r => r.relation === 'personal').map(ref => ({
        name: formatName(ref.name || ''),
        phone: formatPhone(ref.phone || ''),
        relation: toTitleCase(ref.relation || '')
      }));
      if (persRefs.length > 0) setPersonalRefs(persRefs);
    }
    
    setShowAutoFillDialog(false);
    toast.success('Form auto-filled from resume! Please review and complete any missing fields.');
  };

  // Helper to format fields on blur
  const handleFieldBlur = (value, fieldType, setter) => {
    if (!value) return;
    
    let formatted = value;
    switch (fieldType) {
      case 'name':
        formatted = formatName(value);
        break;
      case 'company':
        formatted = formatCompanyName(value);
        break;
      case 'address':
        formatted = formatAddress(value);
        break;
      case 'city':
      case 'designation':
      case 'course':
      case 'university':
      case 'college':
        formatted = toTitleCase(value);
        break;
      case 'pan':
        formatted = formatPAN(value);
        break;
      case 'pincode':
        formatted = formatPincode(value);
        break;
      case 'email':
        formatted = formatEmail(value);
        break;
      case 'phone':
        formatted = formatPhone(value);
        break;
      default:
        break;
    }
    
    if (formatted !== value && setter) {
      setter(formatted);
    }
  };

  // Format address field on blur
  const formatAddressField = (addressType, field, value) => {
    let formatted = value;
    if (field === 'complete_address') {
      formatted = formatAddress(value);
    } else if (field === 'city') {
      formatted = toTitleCase(value);
    } else if (field === 'pincode') {
      formatted = formatPincode(value);
    }
    
    if (addressType === 'present') {
      setPresentAddress(prev => ({ ...prev, [field]: formatted }));
    } else {
      setPermanentAddress(prev => ({ ...prev, [field]: formatted }));
    }
  };

  // Format employment field on blur  
  const formatEmploymentField = (index, field, value) => {
    let formatted = value;
    if (field === 'company_name') {
      formatted = formatCompanyName(value);
    } else if (field === 'designation') {
      formatted = toTitleCase(value);
    } else if (field === 'hr_name') {
      formatted = formatName(value);
    } else if (field === 'hr_email') {
      formatted = formatEmail(value);
    }
    
    setEmploymentEntries(prev => prev.map((entry, i) => 
      i === index ? { ...entry, [field]: formatted } : entry
    ));
  };

  // Format education field on blur
  const formatEducationField = (index, field, value) => {
    let formatted = value;
    if (field === 'course') {
      formatted = formatDegree(value);
    } else if (['university_name', 'college', 'specialization'].includes(field)) {
      formatted = toTitleCase(value);
    }
    
    setEducationEntries(prev => prev.map((entry, i) => 
      i === index ? { ...entry, [field]: formatted } : entry
    ));
  };

  // Format reference field on blur
  const formatReferenceField = (type, index, field, value) => {
    let formatted = value;
    if (field === 'name') {
      formatted = formatName(value);
    } else if (field === 'company') {
      formatted = formatCompanyName(value);
    } else if (field === 'designation' || field === 'relation') {
      formatted = toTitleCase(value);
    } else if (field === 'email') {
      formatted = formatEmail(value);
    } else if (field === 'phone') {
      formatted = formatPhone(value);
    }
    
    if (type === 'professional') {
      setProfessionalRefs(prev => prev.map((ref, i) => 
        i === index ? { ...ref, [field]: formatted } : ref
      ));
    } else {
      setPersonalRefs(prev => prev.map((ref, i) => 
        i === index ? { ...ref, [field]: formatted } : ref
      ));
    }
  };

  const handlePanCardUpload = async (file) => {
    if (file) {
      setPanCardFile(file);
      try {
        const result = await uploadFileToServer(file, 'pan_card', 'id_check');
        if (result) {
          setPanCardUrl(result.file_url);
        }
        toast.success('PAN Card uploaded successfully');
      } catch (error) {
        toast.error('Failed to upload PAN Card');
      }
    }
  };

  const handleAadharCardUpload = async (file) => {
    if (file) {
      setAadharCardFile(file);
      try {
        const result = await uploadFileToServer(file, 'aadhar_card', 'id_check');
        if (result) {
          setAadharCardUrl(result.file_url);
        }
        toast.success('Aadhar Card uploaded successfully');
      } catch (error) {
        toast.error('Failed to upload Aadhar Card');
      }
    }
  };

  // Validate all mandatory fields and return errors
  const validateAllFields = () => {
    const errors = {
      basic: {},
      address: { present: {}, permanent: {} },
      employment: [],
      education: [],
      idDetails: {}
    };
    let hasErrors = false;
    let firstErrorTab = null;

    // Basic Info validation
    if (!gender) {
      errors.basic.gender = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'basic';
    }
    
    // Alternate Contact validation - required unless "no alternate contact" is checked
    if (!noAlternateContact) {
      if (!alternateContact?.trim()) {
        errors.basic.alternateContact = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'basic';
      }
      if (!alternateContactRelation) {
        errors.basic.alternateContactRelation = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'basic';
      }
    }

    // Present Address validation
    // Skip entirely when Address section is locked by backend (bulk-imported by
    // QC/Client) — candidate can't edit these fields, and Address Proof is
    // explicitly not mandatory for the Address Check in this scenario.
    const isAddressLocked = backendLockedSections.includes('address');
    if (!isAddressLocked) {
    if (!presentAddress.complete_address?.trim()) {
      errors.address.present.complete_address = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!presentAddress.city?.trim()) {
      errors.address.present.city = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!presentAddress.state?.trim()) {
      errors.address.present.state = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!presentAddress.pincode?.trim()) {
      errors.address.present.pincode = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!presentAddress.address_proof && !presentAddress.address_proof_url && presentAddress.address_proof_reason !== 'not_available') {
      errors.address.present.address_proof = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }

    // Permanent Address validation
    if (!permanentAddress.complete_address?.trim()) {
      errors.address.permanent.complete_address = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!permanentAddress.city?.trim()) {
      errors.address.permanent.city = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!permanentAddress.state?.trim()) {
      errors.address.permanent.state = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!permanentAddress.pincode?.trim()) {
      errors.address.permanent.pincode = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    if (!permanentAddress.address_proof && !permanentAddress.address_proof_url && permanentAddress.address_proof_reason !== 'not_available') {
      errors.address.permanent.address_proof = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'address';
    }
    }

    // Employment validation (only for experienced category)
    if (isEmploymentRequired) {
      employmentEntries.forEach((emp, index) => {
        const empErrors = {};
        
        if (!emp.company_name?.trim()) {
          empErrors.company_name = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        if (!emp.designation?.trim()) {
          empErrors.designation = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        if (!emp.employed_from) {
          empErrors.employed_from = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        if (emp.employment_type === 'past' && !emp.employed_to) {
          empErrors.employed_to = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        // Validate Employed To is not before Employed From
        if (emp.employed_from && emp.employed_to && new Date(emp.employed_to) < new Date(emp.employed_from)) {
          empErrors.employed_to = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
          toast.error(`Employment ${index + 1}: Employed To date cannot be before Employed From date`);
        }
        // For current employment: employed_to not mandatory when verification permitted and date_of_relieving is provided
        if (emp.employment_type === 'current' && emp.can_verify_current !== false && !emp.employed_to && !emp.date_of_relieving) {
          empErrors.employed_to = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        if (!emp.salary_per_month?.trim()) {
          empErrors.salary_per_month = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        if (!emp.employee_id?.trim()) {
          empErrors.employee_id = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        // UAN/PF mandatory unless marked as not available
        if (!emp.uan_pf_not_available && !emp.uan_pf_number?.trim()) {
          empErrors.uan_pf_number = true;
          hasErrors = true;
          if (!firstErrorTab) firstErrorTab = 'employment';
        }
        // Gap reason if gap detected
        if (index < employmentEntries.length - 1) {
          const nextEmp = employmentEntries[index + 1];
          const currentStart = emp.employed_from;
          const nextEnd = nextEmp.employed_to || nextEmp.date_of_relieving;
          if (currentStart && nextEnd) {
            const gapDays = Math.ceil((new Date(currentStart) - new Date(nextEnd)) / (1000 * 60 * 60 * 24));
            if (gapDays > 30 && !emp.gap_reason) {
              empErrors.gap_reason = true;
              hasErrors = true;
              if (!firstErrorTab) firstErrorTab = 'employment';
            }
          }
        }
        // RM details if mandatory
        if (isRmDetailsMandatory && emp.company_name?.trim()) {
          if (!emp.rm_name?.trim()) {
            empErrors.rm_name = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
          if (!emp.rm_contact_number?.trim()) {
            empErrors.rm_contact_number = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
          if (!emp.rm_email?.trim()) {
            empErrors.rm_email = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
          if (!emp.rm_designation?.trim()) {
            empErrors.rm_designation = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
        }
        // HR Details mandatory unless "Don't have HR details" is checked
        if (!emp.hr_details_not_available && emp.company_name?.trim()) {
          if (!emp.hr_name?.trim()) {
            empErrors.hr_name = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
          if (!emp.hr_email?.trim() || (emp.hr_email && !isValidEmail(emp.hr_email))) {
            empErrors.hr_email = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
          // HR Contact Number is optional — only validate format if provided
          if (emp.hr_contact_number?.trim() && emp.hr_contact_number.length !== 10) {
            empErrors.hr_contact_number = true;
            hasErrors = true;
            if (!firstErrorTab) firstErrorTab = 'employment';
          }
        }
        
        errors.employment[index] = empErrors;
      });
    }

    // Education validation
    educationEntries.forEach((edu, index) => {
      const eduErrors = {};
      
      if (!edu.course?.trim()) {
        eduErrors.course = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'education';
      }
      if (!edu.university_name?.trim()) {
        eduErrors.university_name = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'education';
      }
      if (!edu.year_of_passing) {
        eduErrors.year_of_passing = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'education';
      }
      if (!edu.roll_no?.trim()) {
        eduErrors.roll_no = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'education';
      }
      // Mandatory candidate-side acknowledgment of the specific certificate
      // type being uploaded (Mar 2, 2026 — prevents wrong cert uploads).
      // Only enforced when the case actually has education sub-checks
      // configured; otherwise there's nothing meaningful to acknowledge.
      // Check both packageChecks (authoritative) and verificationCase.checks
      // (fallback for custom-checks cases).
      const _pkgHasEdu = (packageChecks || []).some(pc => {
        const ct = typeof pc === 'string' ? pc : (pc?.check_type || pc?.type);
        return ct === 'education_check';
      });
      const _caseHasEdu = (verificationCase?.checks || []).some(c => c.check_type === 'education_check');
      if ((_pkgHasEdu || _caseHasEdu) && !edu.acknowledged_cert_type) {
        eduErrors.acknowledged_cert_type = true;
        hasErrors = true;
        if (!firstErrorTab) firstErrorTab = 'education';
      }
      
      errors.education[index] = eduErrors;
    });

    // ID Details validation
    if (!panNumber?.trim()) {
      errors.idDetails.panNumber = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'id';
    }
    if (!aadharNumber?.trim()) {
      errors.idDetails.aadharNumber = true;
      hasErrors = true;
      if (!firstErrorTab) firstErrorTab = 'id';
    }

    return { errors, hasErrors, firstErrorTab };
  };

  // Helper to get error class for input fields
  const getErrorClass = (section, field, index = null) => {
    if (!showValidationErrors) return '';
    
    if (section === 'basic') {
      return validationErrors.basic?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'address') {
      const addressType = field.split('.')[0]; // 'present' or 'permanent'
      const fieldName = field.split('.')[1];
      return validationErrors.address?.[addressType]?.[fieldName] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'employment' && index !== null) {
      return validationErrors.employment?.[index]?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'education' && index !== null) {
      return validationErrors.education?.[index]?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'idDetails') {
      return validationErrors.idDetails?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'personalRef' && index !== null) {
      return validationErrors.personalRef?.[index]?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    if (section === 'professionalRef' && index !== null) {
      return validationErrors.professionalRef?.[index]?.[field] ? 'border-red-500 bg-red-50' : '';
    }
    return '';
  };

  const handleSaveDetails = async () => {
    // Validate all fields including references based on package
    const { errors, hasErrors, firstErrorTab } = validateAllFields();
    
    // Add reference validation if checks are in package
    if (hasPersonalRefCheck) {
      errors.personalRef = [];
      const validPersonalRefs = personalRefs.filter(r => r.name?.trim());
      if (validPersonalRefs.length === 0) {
        errors.personalRef[0] = { name: true, phone: true };
        errors.hasRefErrors = true;
        if (!errors.firstErrorTab) errors.firstErrorTab = 'references';
      } else {
        personalRefs.forEach((ref, idx) => {
          const refErrors = {};
          if (ref.name?.trim() && !ref.phone?.trim()) {
            refErrors.phone = true;
            errors.hasRefErrors = true;
          }
          errors.personalRef[idx] = refErrors;
        });
      }
    }
    
    if (hasProfessionalRefCheck) {
      errors.professionalRef = [];
      const validProfRefs = professionalRefs.filter(r => r.name?.trim());
      if (validProfRefs.length === 0) {
        errors.professionalRef[0] = { name: true, phone: true, email: true, designation: true };
        errors.hasRefErrors = true;
        if (!errors.firstErrorTab) errors.firstErrorTab = 'references';
      } else {
        professionalRefs.forEach((ref, idx) => {
          const refErrors = {};
          if (ref.name?.trim()) {
            if (!ref.phone?.trim()) { refErrors.phone = true; errors.hasRefErrors = true; }
            if (!ref.email?.trim()) { refErrors.email = true; errors.hasRefErrors = true; }
            if (!ref.designation?.trim()) { refErrors.designation = true; errors.hasRefErrors = true; }
          }
          errors.professionalRef[idx] = refErrors;
        });
      }
    }
    
    const totalHasErrors = hasErrors || errors.hasRefErrors;
    
    if (totalHasErrors) {
      setValidationErrors(errors);
      setShowValidationErrors(true);
      
      // Navigate to the first tab with errors
      const errorTab = firstErrorTab || errors.firstErrorTab;
      if (errorTab) {
        setDetailsTab(errorTab);
      }
      
      // Show toast with error summary
      toast.error('Please fill all required fields before saving. Check highlighted fields.');
      return;
    }
    
    // Clear validation errors on successful validation
    setShowValidationErrors(false);
    setValidationErrors({ basic: {}, address: { present: {}, permanent: {} }, employment: [], education: [], idDetails: {} });

    // PACKAGE QUANTITY GUARD: prevent saving more entries than the package allows
    {
      const reqEmp = getRequiredCountForCheckType('employment_check');
      const filledEmp = employmentEntries.filter(e => e.company_name && e.company_name.trim() !== '').length;
      if (reqEmp > 0 && filledEmp > reqEmp) {
        toast.error(`Your verification package allows only ${reqEmp} employment ${reqEmp === 1 ? 'entry' : 'entries'}, but you have ${filledEmp}. Please remove the extra ${filledEmp - reqEmp === 1 ? 'entry' : 'entries'} before saving.`);
        setDetailsTab('employment');
        return;
      }
      const reqEdu = getRequiredCountForCheckType('education_check');
      const filledEdu = educationEntries.filter(e => e.course && e.course.trim() !== '').length;
      if (reqEdu > 0 && filledEdu > reqEdu) {
        toast.error(`Your verification package allows only ${reqEdu} education ${reqEdu === 1 ? 'entry' : 'entries'}, but you have ${filledEdu}. Please remove the extra ${filledEdu - reqEdu === 1 ? 'entry' : 'entries'} before saving.`);
        setDetailsTab('education');
        return;
      }
    }

    // Validate RM details if mandatory (only for experienced)
    if (isRmDetailsMandatory && isEmploymentRequired) {
      const invalidEmployments = employmentEntries.filter(emp => {
        if (!emp.company_name.trim()) return false; // Skip empty entries
        return !emp.rm_name || !emp.rm_contact_number || !emp.rm_email || !emp.rm_designation;
      });
      
      if (invalidEmployments.length > 0) {
        toast.error('Please fill in all mandatory Reference 1 details (Name, Contact, Email, Designation) for each employment as Professional Reference check is required');
        setDetailsTab('employment');
        return;
      }
    }
    
    // Auto-copy RM details to Professional References if package requires professional reference
    let finalProfessionalRefs = [...professionalRefs];
    if (isRmDetailsMandatory && isEmploymentRequired) {
      const validEmployments = employmentEntries.filter(e => e.company_name.trim() !== '');
      let copiedCount = 0;
      
      validEmployments.forEach((emp) => {
        // Copy Reference 1 (RM1)
        if (emp.rm_name) {
          const alreadyExists = finalProfessionalRefs.some(
            ref => ref.name === emp.rm_name && ref.phone === emp.rm_contact_number
          );
          
          if (!alreadyExists) {
            const emptyIndex = finalProfessionalRefs.findIndex(ref => !ref.name && !ref.phone);
            if (emptyIndex >= 0) {
              finalProfessionalRefs[emptyIndex] = {
                name: emp.rm_name || '',
                phone: emp.rm_contact_number || '',
                designation: emp.rm_designation || '',
                company: emp.rm_company || emp.company_name || '',
                email: emp.rm_email || ''
              };
              copiedCount++;
            }
          }
        }
        
        // Copy Reference 2 (RM2)
        if (emp.rm2_name) {
          const alreadyExists = finalProfessionalRefs.some(
            ref => ref.name === emp.rm2_name && ref.phone === emp.rm2_contact_number
          );
          
          if (!alreadyExists) {
            const emptyIndex = finalProfessionalRefs.findIndex(ref => !ref.name && !ref.phone);
            if (emptyIndex >= 0) {
              finalProfessionalRefs[emptyIndex] = {
                name: emp.rm2_name || '',
                phone: emp.rm2_contact_number || '',
                designation: emp.rm2_designation || '',
                company: emp.rm2_company || emp.company_name || '',
                email: emp.rm2_email || ''
              };
              copiedCount++;
            }
          }
        }
      });
      
      if (copiedCount > 0) {
        setProfessionalRefs(finalProfessionalRefs);
        toast.info(`${copiedCount} reference(s) auto-copied to Professional References`);
      }
    }
    
    try {
      await axios.put(`/employees/${employee.id}/details`, {
        gender,
        alternate_contact: alternateContact,
        alternate_contact_relation: alternateContactRelation,
        no_alternate_contact: noAlternateContact,
        present_address: presentAddress,
        permanent_address: permanentAddress,
        permanent_same_as_present: permanentSameAsPresent,
        additional_addresses: additionalAddresses.filter(a => a.complete_address.trim() !== ''),
        pan_number: panNumber,
        aadhar_number: aadharNumber,
        pan_card_url: panCardUrl,
        aadhar_card_url: aadharCardUrl,
        employee_category: employeeCategory,
        is_fresher: !isEmploymentRequired, // Backward compatibility
        employment_entries: isEmploymentRequired ? employmentEntries.filter(e => e.company_name.trim() !== '') : [],
        education_entries: educationEntries.filter(e => e.course.trim() !== ''),
        personal_references: personalRefs.filter(r => r.name.trim() !== ''),
        professional_references: finalProfessionalRefs.filter(r => r.name.trim() !== '')
      });
      toast.success('Details saved successfully!');
      fetchEmployeeData();
    } catch (error) {
      // Surface the backend's specific error message (e.g., package
      // quantity guard) instead of a generic "Failed to save" so the
      // candidate knows exactly what to fix.
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      toast.error(detail || 'Failed to save details. Please try again.', { duration: 8000 });
    }
  };

  // Signature canvas drawing functions
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Get coordinates from mouse or touch event
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Scale coordinates to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = signatureCanvasRef.current;
      if (canvas) {
        setSignatureData(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  const handleSignDeclaration = async () => {
    try {
      await axios.post(`/employees/${employee.id}/declaration`, {
        employee_id: employee.id,
        signature: signatureData || user.name,
        agreed: declarationAgreed
      });
      toast.success('Declaration signed successfully!');
      setDeclarationSigned(true);
      fetchEmployeeData();
    } catch (error) {
      toast.error('Failed to sign declaration');
    }
  };

  // Calculate progress for data entry based on package checks
  const calculateProgress = useMemo(() => {
    if (packageChecks.length === 0) {
      return { completed: 0, total: 0, percentage: 0, details: [] };
    }

    let totalChecks = 0;
    let completedChecks = 0;
    const details = [];

    for (const check of packageChecks) {
      const checkType = check.check_type || check;
      const requiredCount = check.quantity || 1;

      // Calculate based on check type
      if (checkType === 'employment_check') {
        totalChecks += requiredCount;
        const filledCount = employmentEntries.filter(e => e.company_name.trim() !== '').length;
        const completed = employmentDeclaration ? requiredCount : Math.min(filledCount, requiredCount);
        completedChecks += completed;
        details.push({
          type: 'Employment',
          icon: 'briefcase',
          required: requiredCount,
          filled: filledCount,
          completed,
          hasDeclaration: employmentDeclaration,
          status: completed >= requiredCount || employmentDeclaration ? 'complete' : filledCount > 0 ? 'partial' : 'pending'
        });
      } else if (checkType === 'education_check') {
        totalChecks += requiredCount;
        const filledCount = educationEntries.filter(e => e.course.trim() !== '').length;
        const completed = educationDeclaration ? requiredCount : Math.min(filledCount, requiredCount);
        completedChecks += completed;
        details.push({
          type: 'Education',
          icon: 'graduation',
          required: requiredCount,
          filled: filledCount,
          completed,
          hasDeclaration: educationDeclaration,
          status: completed >= requiredCount || educationDeclaration ? 'complete' : filledCount > 0 ? 'partial' : 'pending'
        });
      } else if (checkType === 'address_check') {
        totalChecks += requiredCount;
        const additionalFilledCount = additionalAddresses.filter(a => a.complete_address.trim() !== '').length;
        const filledCount = (presentAddress.complete_address ? 1 : 0) + (permanentAddress.complete_address ? 1 : 0) + additionalFilledCount;
        const completed = addressDeclaration ? requiredCount : Math.min(filledCount, requiredCount);
        completedChecks += completed;
        details.push({
          type: 'Address',
          icon: 'map',
          required: requiredCount,
          filled: filledCount,
          completed,
          hasDeclaration: addressDeclaration,
          status: completed >= requiredCount || addressDeclaration ? 'complete' : filledCount > 0 ? 'partial' : 'pending'
        });
      } else if (checkType === 'professional_reference') {
        totalChecks += requiredCount;
        const filledCount = professionalRefs.filter(r => r.name.trim() !== '').length;
        const completed = Math.min(filledCount, requiredCount);
        completedChecks += completed;
        details.push({
          type: 'Professional Reference',
          icon: 'users',
          required: requiredCount,
          filled: filledCount,
          completed,
          hasDeclaration: false,
          status: completed >= requiredCount ? 'complete' : filledCount > 0 ? 'partial' : 'pending'
        });
      } else if (checkType === 'personal_reference' || checkType === 'reference_check') {
        totalChecks += requiredCount;
        const filledCount = personalRefs.filter(r => r.name.trim() !== '').length;
        const completed = Math.min(filledCount, requiredCount);
        completedChecks += completed;
        details.push({
          type: checkType === 'reference_check' ? 'Reference Check' : 'Personal Reference',
          icon: 'users',
          required: requiredCount,
          filled: filledCount,
          completed,
          hasDeclaration: false,
          status: completed >= requiredCount ? 'complete' : filledCount > 0 ? 'partial' : 'pending'
        });
      } else if (checkType === 'id_check') {
        totalChecks += 1;
        // ID check needs PAN or Aadhar
        const isComplete = panNumber.trim() !== '' || aadharNumber.trim() !== '';
        if (isComplete) completedChecks += 1;
        details.push({
          type: 'ID Verification',
          icon: 'credit-card',
          required: 1,
          filled: isComplete ? 1 : 0,
          completed: isComplete ? 1 : 0,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Copied from Basic Details'
        });
      } else if (checkType === 'criminal_court_check' || checkType === 'criminal_advocate_check') {
        totalChecks += requiredCount;
        // Criminal checks copy from Present Address
        const isComplete = presentAddress.complete_address.trim() !== '';
        const completed = isComplete ? requiredCount : 0;
        completedChecks += completed;
        details.push({
          type: checkType === 'criminal_court_check' ? 'Criminal Court Check' : 'Criminal Advocate Check',
          icon: 'shield',
          required: requiredCount,
          filled: isComplete ? requiredCount : 0,
          completed,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Copied from Present Address'
        });
      } else if (checkType === 'world_criminal_db' || checkType === 'india_criminal_db') {
        totalChecks += 1;
        // Database checks copy from Basic Details
        const isComplete = employee?.name && employee?.dob && employee?.father_name;
        if (isComplete) completedChecks += 1;
        details.push({
          type: checkType === 'world_criminal_db' ? 'World Criminal Database' : 'India Criminal Database',
          icon: 'database',
          required: 1,
          filled: isComplete ? 1 : 0,
          completed: isComplete ? 1 : 0,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Copied from Basic Details'
        });
      } else if (checkType === 'credit_check') {
        totalChecks += 1;
        // Credit check needs Basic + PAN
        const isComplete = employee?.name && panNumber.trim() !== '';
        if (isComplete) completedChecks += 1;
        details.push({
          type: 'Credit Check',
          icon: 'credit-card',
          required: 1,
          filled: isComplete ? 1 : 0,
          completed: isComplete ? 1 : 0,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Copied from Basic Details + PAN'
        });
      } else if (checkType === 'drug_test') {
        totalChecks += 1;
        // Drug test needs basic details
        const isComplete = !!employee?.name;
        if (isComplete) completedChecks += 1;
        details.push({
          type: 'Drug Test',
          icon: 'flask',
          required: 1,
          filled: isComplete ? 1 : 0,
          completed: isComplete ? 1 : 0,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Copied from Basic Details'
        });
      } else {
        // Other check types - auto-filled from basic details
        totalChecks += 1;
        const isComplete = !!employee?.name;
        if (isComplete) completedChecks += 1;
        details.push({
          type: checkType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: 'check',
          required: 1,
          filled: isComplete ? 1 : 0,
          completed: isComplete ? 1 : 0,
          hasDeclaration: false,
          status: isComplete ? 'complete' : 'pending',
          note: 'Auto-filled'
        });
      }
    }

    const percentage = totalChecks > 0 ? Math.round((completedChecks / totalChecks) * 100) : 0;
    return { completed: completedChecks, total: totalChecks, percentage, details };
  }, [packageChecks, employmentEntries, educationEntries, presentAddress, permanentAddress, additionalAddresses,
      professionalRefs, personalRefs, panNumber, aadharNumber, employee,
      employmentDeclaration, educationDeclaration, addressDeclaration]);

  // Check if all required checks are complete or have declarations
  const canSubmit = () => {
    if (packageChecks.length === 0) return false;
    
    for (const check of packageChecks) {
      const checkType = check.check_type || check;
      const requiredCount = check.quantity || 1;
      let filledCount = 0;
      let declaration = false;
      
      if (checkType === 'employment_check') {
        filledCount = employmentEntries.filter(e => e.company_name.trim() !== '').length;
        declaration = employmentDeclaration;
        if (filledCount === 0) return false;
        if (filledCount < requiredCount && !declaration) return false;
        // PACKAGE QUANTITY GUARD: block submission if extras present
        if (filledCount > requiredCount) return false;
      } else if (checkType === 'education_check') {
        filledCount = educationEntries.filter(e => e.course.trim() !== '').length;
        declaration = educationDeclaration;
        if (filledCount === 0) return false;
        if (filledCount < requiredCount && !declaration) return false;
        // PACKAGE QUANTITY GUARD: block submission if extras present
        if (filledCount > requiredCount) return false;
      } else if (checkType === 'address_check') {
        // Count present + permanent + additional addresses
        const additionalFilledCount = additionalAddresses.filter(a => a.complete_address.trim() !== '').length;
        filledCount = (presentAddress.complete_address ? 1 : 0) + (permanentAddress.complete_address ? 1 : 0) + additionalFilledCount;
        declaration = addressDeclaration;
        if (filledCount === 0) return false;
        if (filledCount < requiredCount && !declaration) return false;
      } else if (checkType === 'professional_reference') {
        filledCount = professionalRefs.filter(r => r.name.trim() !== '').length;
        if (filledCount < requiredCount) return false;
      } else if (checkType === 'personal_reference' || checkType === 'reference_check') {
        filledCount = personalRefs.filter(r => r.name.trim() !== '').length;
        if (filledCount < requiredCount) return false;
      } else if (checkType === 'id_check') {
        // At least PAN or Aadhar should be filled
        if (!panNumber && !aadharNumber) return false;
      } else if (checkType === 'criminal_court_check' || checkType === 'criminal_advocate_check') {
        // Criminal checks copy from Present Address - need at least present address filled
        if (!presentAddress.complete_address) return false;
      } else if (checkType === 'world_criminal_db' || checkType === 'india_criminal_db') {
        // World/India Database copies Basic Details (name, DOB, father name)
        if (!employee?.name || !employee?.dob || !employee?.father_name) return false;
      } else if (checkType === 'credit_check') {
        // Credit Check copies Basic Details + PAN
        if (!employee?.name || !panNumber) return false;
      } else if (checkType === 'drug_test') {
        // Drug test - just needs basic details
        if (!employee?.name) return false;
      }
      // Other automated checks are auto-filled
    }
    
    return true;
  };

  // Handle final submission
  const handleFinalSubmit = async () => {
    if (!canSubmit()) {
      // Be specific if the reason is excess entries
      const reqEmp = getRequiredCountForCheckType('employment_check');
      const filledEmp = employmentEntries.filter(e => e.company_name && e.company_name.trim() !== '').length;
      if (reqEmp > 0 && filledEmp > reqEmp) {
        toast.error(`Your package allows only ${reqEmp} employment ${reqEmp === 1 ? 'entry' : 'entries'}. Please remove ${filledEmp - reqEmp} extra before submitting.`);
        setDetailsTab('employment');
        return;
      }
      const reqEdu = getRequiredCountForCheckType('education_check');
      const filledEdu = educationEntries.filter(e => e.course && e.course.trim() !== '').length;
      if (reqEdu > 0 && filledEdu > reqEdu) {
        toast.error(`Your package allows only ${reqEdu} education ${reqEdu === 1 ? 'entry' : 'entries'}. Please remove ${filledEdu - reqEdu} extra before submitting.`);
        setDetailsTab('education');
        return;
      }
      toast.error('Please complete all required checks before submitting');
      return;
    }
    
    try {
      // First save all details with declarations
      await axios.put(`/employees/${employee.id}/details`, {
        gender,
        alternate_contact: alternateContact,
        present_address: presentAddress,
        permanent_address: permanentAddress,
        permanent_same_as_present: permanentSameAsPresent,
        additional_addresses: additionalAddresses.filter(a => a.complete_address.trim() !== ''),
        pan_number: panNumber,
        aadhar_number: aadharNumber,
        employee_category: employeeCategory,
        is_fresher: !isEmploymentRequired, // Backward compatibility
        employment_entries: isEmploymentRequired ? employmentEntries.filter(e => e.company_name.trim() !== '') : [],
        education_entries: educationEntries.filter(e => e.course.trim() !== ''),
        personal_references: personalRefs.filter(r => r.name.trim() !== ''),
        professional_references: professionalRefs.filter(r => r.name.trim() !== ''),
        employment_declaration: employmentDeclaration,
        education_declaration: educationDeclaration,
        address_declaration: addressDeclaration,
        details_filled: true,
        submitted_at: new Date().toISOString()
      });
      
      // Then sign the declaration
      await axios.post(`/employees/${employee.id}/declaration`, {
        employee_id: employee.id,
        signature: user.name
      });
      
      toast.success('Your details have been submitted successfully for verification!');
      setDeclarationSigned(true);
      fetchEmployeeData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      toast.error(detail || 'Failed to submit details. Please try again.', { duration: 8000 });
    }
  };

  const getOverallStatus = () => {
    if (!verificationCase) return 'Not Started';
    if (verificationCase.status === 'completed') return 'Completed';
    if (verificationCase.status === 'in_progress') return 'In Progress';
    if (verificationCase.status === 'pending_qc') return 'Under Review';
    return 'Pending';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Completed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'In Progress': 'bg-amber-100 text-amber-700 border-amber-200',
      'Under Review': 'bg-blue-100 text-blue-700 border-blue-200',
      'Pending': 'bg-slate-100 text-slate-700 border-slate-200',
      'Not Started': 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return statusMap[status] || statusMap['Pending'];
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-slate-900" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Employee Portal</h1>
              {verificationCase && (
                <p className="text-xs text-slate-500 mono">Case ID: {verificationCase.id}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Insufficiency Action Required Banner - Priority over other banners */}
        {hasOpenInsufficiencies() && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6 flex items-center justify-between" data-testid="insufficiency-action-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
              <div>
                <p className="font-semibold text-rose-900">Insufficiency Raised - Action Required</p>
                <p className="text-sm text-rose-700">
                  The QC team has raised {getTotalInsufficiencyCount()} insufficiency(ies). Please review and provide the required information to proceed with your verification.
                </p>
              </div>
            </div>
            <Button 
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => {
                setActiveTab('insufficiencies');
              }}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Take Action ({getTotalInsufficiencyCount()})
            </Button>
          </div>
        )}
        
        {/* Submission Status Banner - Only show if no insufficiencies */}
        {!declarationSigned && !hasOpenInsufficiencies() && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-900">Submission Pending</p>
                <p className="text-sm text-red-700">Complete all required details and click "Review & Submit" to finalize your verification</p>
              </div>
            </div>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                setActiveTab('profile');
                setDetailsTab('review');
              }}
            >
              Go to Review & Submit
            </Button>
          </div>
        )}
        
        {/* Submission Complete Banner - Only show if no insufficiencies */}
        {declarationSigned && !hasOpenInsufficiencies() && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-900">Submission Complete</p>
              <p className="text-sm text-emerald-700">Your details have been submitted successfully. The verification process is in progress.</p>
            </div>
          </div>
        )}

        {/* Form Locked Banner - Show when form is locked, no insufficiencies, and no active insufficiency clearing */}
        {isFormLocked() && !hasOpenInsufficiencies() && !activeInsufficiencyClearing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between" data-testid="form-locked-banner">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">Form Locked</p>
                <p className="text-sm text-amber-700">
                  Your details have been submitted and are under review. You cannot edit your information unless there is an insufficiency raised by the QC team.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Partial Submission Banner - Process Basic Verification First workflow */}
        {partialSubmissionMode && !hasOpenInsufficiencies() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between" data-testid="partial-submission-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900">Complete Your Remaining Details</p>
                <p className="text-sm text-blue-700">
                  Your basic verification is being processed. Please complete the remaining sections (Employment, Education, ID Details, References) and submit to finalize your verification.
                </p>
              </div>
            </div>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                setActiveTab('profile');
                setDetailsTab('review');
              }}
            >
              Complete & Submit
            </Button>
          </div>
        )}
        
        {/* QC Approved Sections Banner - Show when some sections are locked by QC approval */}
        {backendLockedSections.length > 0 && !isFormLocked() && (
          <div className={`rounded-lg p-4 mb-6 flex items-center gap-3 ${
            basicLockedAfterSubmission && backendLockedSections.length === 1 && backendLockedSections.includes('basic')
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-blue-50 border border-blue-200'
          }`} data-testid="qc-approved-banner">
            <CheckCircle className={`w-6 h-6 ${
              basicLockedAfterSubmission && backendLockedSections.length === 1 && backendLockedSections.includes('basic')
                ? 'text-amber-600'
                : 'text-blue-600'
            }`} />
            <div>
              <p className={`font-semibold ${
                basicLockedAfterSubmission && backendLockedSections.length === 1 && backendLockedSections.includes('basic')
                  ? 'text-amber-900'
                  : 'text-blue-900'
              }`}>
                {basicLockedAfterSubmission && backendLockedSections.length === 1 && backendLockedSections.includes('basic')
                  ? 'Basic Information Locked'
                  : 'Some Sections Locked'}
              </p>
              <p className={`text-sm ${
                basicLockedAfterSubmission && backendLockedSections.length === 1 && backendLockedSections.includes('basic')
                  ? 'text-amber-700'
                  : 'text-blue-700'
              }`}>
                {basicLockedAfterSubmission && backendLockedSections.includes('basic')
                  ? <>Your <strong>Basic</strong> information is locked after initial submission to ensure data integrity.
                    {backendLockedSections.filter(s => s !== 'basic').length > 0 && (
                      <> Other sections approved by QC: <strong>{backendLockedSections.filter(s => s !== 'basic').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}</strong></>
                    )}
                  </>
                  : <>The following sections have been approved by QC and are now locked: <strong>{backendLockedSections.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}</strong></>
                }
              </p>
            </div>
          </div>
        )}

        {/* Active Insufficiency Clearing Banner */}
        {activeInsufficiencyClearing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between" data-testid="clearing-insufficiency-banner">
            <div className="flex items-center gap-3">
              <Edit className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900">Editing Enabled - Clearing Insufficiency</p>
                <p className="text-sm text-blue-700">
                  You can now edit the <strong>{activeInsufficiencyClearing}</strong> section to provide the required information. 
                  Save your changes when done.
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-blue-400 text-blue-700 hover:bg-blue-100"
              onClick={cancelClearingInsufficiency}
            >
              Cancel Editing
            </Button>
          </div>
        )}

        {/* Data Entry Progress Bar */}
        {packageChecks.length > 0 && !declarationSigned && (
          <Card className="mb-6 border-slate-200 shadow-sm" data-testid="progress-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Your Form Progress
                </CardTitle>
                <Badge 
                  variant={calculateProgress.percentage === 100 ? "default" : "secondary"}
                  className={calculateProgress.percentage === 100 ? "bg-emerald-500" : ""}
                >
                  {calculateProgress.completed} / {calculateProgress.total} Sections Filled
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">Track your form completion status. This is NOT verification progress.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Form Completion</span>
                  <span className={`font-semibold ${calculateProgress.percentage === 100 ? 'text-emerald-600' : 'text-slate-900'}`}>
                    {calculateProgress.percentage}%
                  </span>
                </div>
                <Progress 
                  value={calculateProgress.percentage} 
                  className="h-3"
                />
              </div>

              {/* Check Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                {calculateProgress.details.map((detail, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      detail.status === 'complete' 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : detail.status === 'partial' 
                          ? 'bg-amber-50 border-amber-200' 
                          : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${
                        detail.status === 'complete' 
                          ? 'text-emerald-700' 
                          : detail.status === 'partial' 
                            ? 'text-amber-700' 
                            : 'text-slate-600'
                      }`}>
                        {detail.type}
                      </span>
                      {detail.status === 'complete' ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : detail.status === 'partial' ? (
                        <Clock className="w-4 h-4 text-amber-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        {detail.filled}/{detail.required} filled
                        {detail.hasDeclaration && ' (N/A declared)'}
                      </span>
                      {detail.note && (
                        <span className="text-slate-400 italic">{detail.note}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Helpful Tips */}
              {calculateProgress.percentage < 100 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-blue-700">
                    <strong>Tip:</strong> Checks marked as "Copied from..." will auto-fill once you save the source data. 
                    If a check is not applicable, you can declare it as "N/A" during Review & Submit.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="mb-8 border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">Verification Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <Badge className={`${getStatusBadge(getOverallStatus())} px-4 py-2 text-base`} data-testid="verification-status">
                {getOverallStatus()}
              </Badge>
              {hasOpenInsufficiencies() && (
                <Badge className="bg-rose-100 text-rose-700 border border-rose-300 px-4 py-2 text-base" data-testid="insufficiency-status">
                  <AlertTriangle className="w-4 h-4 mr-2 inline" />
                  Action Required - {getTotalInsufficiencyCount()} Insufficiency(ies)
                </Badge>
              )}
              {!employee?.details_filled && !hasOpenInsufficiencies() && (
                <p className="text-slate-600">Please fill in your details to start the verification process.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'profile' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="profile-tab"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            My Details
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'documents' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="documents-tab"
          >
            <Upload className="w-4 h-4 inline mr-2" />
            My Documents
          </button>
          <button
            onClick={() => setActiveTab('insufficiencies')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 relative ${
              activeTab === 'insufficiencies' ? 'border-rose-600 text-rose-600' : hasOpenInsufficiencies() ? 'border-transparent text-rose-600 hover:text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="insufficiency-tab"
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Action Required
            {hasOpenInsufficiencies() && (
              <span className="ml-2 bg-rose-600 text-white text-xs px-2 py-0.5 rounded-full">
                {getTotalInsufficiencyCount()}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('declaration')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'declaration' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="declaration-tab"
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Declaration
          </button>
        </div>

        {activeTab === 'documents' && (
          <EmployeeDocumentsTab employeeId={employee?.id} caseId={employee?.case_id} />
        )}

        {activeTab === 'insufficiencies' && (
          <EmployeeInsufficiencyTab 
            employeeId={employee?.id} 
            caseId={employee?.case_id}
            systemInsufficiencies={systemInsufficiencies}
          />
        )}

        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Inner Tabs for My Details */}
            <Tabs value={detailsTab} onValueChange={setDetailsTab} className="w-full">
              <TabsList className="grid w-full grid-cols-7 mb-4">
                <TabsTrigger value="basic" className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Basic Info</span>
                </TabsTrigger>
                <TabsTrigger value="id" className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="hidden sm:inline">ID Details</span>
                </TabsTrigger>
                <TabsTrigger value="address" className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span className="hidden sm:inline">Address</span>
                </TabsTrigger>
                <TabsTrigger value="employment" className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span className="hidden sm:inline">Employment</span>
                  {hasEmploymentCheck && isEmploymentRequired && <span className="text-red-500 text-xs">*</span>}
                </TabsTrigger>
                <TabsTrigger value="references" className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">References</span>
                  {(hasPersonalRefCheck || hasProfessionalRefCheck) && <span className="text-red-500 text-xs">*</span>}
                </TabsTrigger>
                <TabsTrigger value="education" className="flex items-center gap-1">
                  <GraduationCap className="w-4 h-4" />
                  <span className="hidden sm:inline">Education</span>
                </TabsTrigger>
                <TabsTrigger value="review" className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Review & Submit</span>
                </TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4">
                {/* Edit Approved Banner - Show when edit is approved */}
                {basicEditApproved && (
                  <div className="rounded-lg p-4 bg-green-50 border border-green-200" data-testid="edit-approved-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-700">
                        <Edit className="w-5 h-5" />
                        <div>
                          <p className="font-semibold">Edit Mode Active</p>
                          <p className="text-sm">You can now edit your basic information. Click "Done Editing" when finished.</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="border-green-400 text-green-700 hover:bg-green-100"
                        onClick={handleCompleteEdit}
                        data-testid="done-editing-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Done Editing
                      </Button>
                    </div>
                  </div>
                )}

                {/* Pending Edit Request Banner */}
                {pendingEditRequest && pendingEditRequest.status === 'pending' && !basicEditApproved && (
                  <div className="rounded-lg p-4 bg-yellow-50 border border-yellow-200" data-testid="pending-request-banner">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <Clock className="w-5 h-5" />
                      <div>
                        <p className="font-semibold">Edit Request Pending</p>
                        <p className="text-sm">Your request to edit {pendingEditRequest.fields_to_edit?.join(', ')} is awaiting approval.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rejected Edit Request Banner */}
                {pendingEditRequest && pendingEditRequest.status === 'rejected' && (
                  <div className="rounded-lg p-4 bg-red-50 border border-red-200 mb-2" data-testid="rejected-request-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        <div>
                          <p className="font-semibold">Edit Request Rejected</p>
                          <p className="text-sm">{pendingEditRequest.review_notes || 'Your request was not approved.'}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-400 text-red-700 hover:bg-red-100"
                        onClick={() => setShowEditRequestDialog(true)}
                      >
                        Request Again
                      </Button>
                    </div>
                  </div>
                )}

                {/* Basic Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('basic')) && !canEditSection('basic') && !basicEditApproved && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${
                    basicLockedAfterSubmission && !isSectionLockedByBackend('basic') 
                      ? 'bg-amber-50 border border-amber-200' 
                      : isSectionLockedByBackend('basic') 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'bg-slate-100 border border-slate-300'
                  }`}>
                    <div className={`flex items-center gap-2 ${
                      basicLockedAfterSubmission && !isSectionLockedByBackend('basic')
                        ? 'text-amber-700'
                        : isSectionLockedByBackend('basic') 
                          ? 'text-blue-700' 
                          : 'text-slate-600'
                    }`}>
                      <Lock className="w-5 h-5" />
                      <span>{
                        basicLockedAfterSubmission && backendLockedSections.includes('basic')
                          ? 'Basic information is locked after initial submission to ensure data integrity during verification.'
                          : isSectionLockedByBackend('basic') && !basicLockedAfterSubmission
                            ? 'This section has been approved by QC and is locked for verification.'
                            : basicLockedAfterSubmission
                              ? 'Basic information is locked after initial submission to ensure data integrity during verification.'
                              : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                    {/* Request Edit Button - Only show if locked due to initial submission */}
                    {basicLockedAfterSubmission && !pendingEditRequest?.status?.includes('pending') && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => setShowEditRequestDialog(true)}
                        data-testid="request-edit-btn"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Request Edit
                      </Button>
                    )}
                  </div>
                )}
                
                <div className={(isFormLocked() && !canEditSection('basic') && !basicEditApproved) ? 'opacity-60 pointer-events-none' : ''}>
                {/* Photo Upload Section */}
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <div className="flex-shrink-0">
                        {photoPreview ? (
                          <img
                            src={photoPreview}
                            alt="Profile"
                            className="w-32 h-32 rounded-lg object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-32 h-32 rounded-lg bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                            <User className="w-12 h-12 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-2">Passport Size Photo *</h3>
                        <p className="text-sm text-slate-600 mb-3">Upload a recent passport size photograph (JPG/PNG, max 2MB)</p>
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/jpeg,image/png,image/jpg"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(e.target.files[0])}
                        />
                        <label htmlFor="photo-upload">
                          <Button variant="outline" size="sm" asChild className="cursor-pointer">
                            <span>
                              <Upload className="w-4 h-4 mr-2" />
                              {photoFile ? 'Change Photo' : 'Upload Photo'}
                            </span>
                          </Button>
                        </label>
                        {photoFile && (
                          <span className="ml-3 text-sm text-emerald-600">✓ {photoFile.name}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Basic Information */}
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={employee?.name || ''} disabled className="bg-slate-50" />
                      </div>
                      <div>
                        <Label>Father's Name</Label>
                        <Input value={employee?.father_name || ''} disabled className="bg-slate-50" />
                      </div>
                      <div>
                        <Label>Date of Birth</Label>
                        <Input value={employee?.dob || ''} disabled className="bg-slate-50" />
                      </div>
                      <div>
                        <Label>Gender *</Label>
                        <select
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          disabled={isFormLocked() && !canEditSection('basic')}
                          className={`w-full px-3 py-2 border rounded-md ${isFormLocked() && !canEditSection('basic') ? 'bg-slate-100 cursor-not-allowed' : ''} ${getErrorClass('basic', 'gender') || 'border-slate-200'}`}
                          data-testid="gender-select"
                        >
                          <option value="">Select</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {showValidationErrors && validationErrors.basic.gender && (
                          <p className="text-xs text-red-600 mt-1">Gender is required</p>
                        )}
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input value={employee?.email || ''} disabled className="bg-slate-50" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={employee?.phone || ''} disabled className="bg-slate-50" />
                      </div>
                    </div>
                    
                    {/* Alternate Contact Section */}
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <Label className="font-medium mb-3 block">Alternate Contact Number *</Label>
                      
                      {/* Don't have alternate number checkbox */}
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          id="no-alternate-contact"
                          checked={noAlternateContact}
                          disabled={isFormLocked() && !canEditSection('basic')}
                          onChange={(e) => {
                            setNoAlternateContact(e.target.checked);
                            if (e.target.checked) {
                              setAlternateContact('');
                              setAlternateContactRelation('');
                            }
                          }}
                          className={`w-4 h-4 rounded border-slate-300 ${isFormLocked() && !canEditSection('basic') ? 'cursor-not-allowed opacity-50' : ''}`}
                        />
                        <Label htmlFor="no-alternate-contact" className="text-sm font-normal cursor-pointer text-slate-600">
                          I don't have an alternate contact number
                        </Label>
                      </div>
                      
                      {!noAlternateContact && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm mb-1 block">Alternate Phone Number * (10 digits)</Label>
                            <Input
                              value={alternateContact}
                              onChange={(e) => setAlternateContact(formatPhoneMax10(e.target.value))}
                              placeholder="Enter 10 digit phone number"
                              maxLength={10}
                              disabled={isFormLocked() && !canEditSection('basic')}
                              className={`${isFormLocked() && !canEditSection('basic') ? 'bg-slate-100 cursor-not-allowed' : ''} ${showValidationErrors && (!alternateContact || alternateContact.length !== 10) && !noAlternateContact ? 'border-red-400' : ''}`}
                            />
                            {alternateContact && alternateContact.length !== 10 && (
                              <p className="text-xs text-red-500 mt-1">Phone number must be exactly 10 digits</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-sm mb-1 block">Relationship *</Label>
                            <Select value={alternateContactRelation} onValueChange={setAlternateContactRelation} disabled={isFormLocked() && !canEditSection('basic')}>
                              <SelectTrigger className={`${isFormLocked() && !canEditSection('basic') ? 'bg-slate-100 cursor-not-allowed' : ''} ${showValidationErrors && !alternateContactRelation && !noAlternateContact ? 'border-red-400' : ''}`}>
                                <SelectValue placeholder="Select relationship" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="father">Father</SelectItem>
                                <SelectItem value="mother">Mother</SelectItem>
                                <SelectItem value="brother">Brother</SelectItem>
                                <SelectItem value="sister">Sister</SelectItem>
                                <SelectItem value="relative">Relative</SelectItem>
                                <SelectItem value="friend">Friend</SelectItem>
                                <SelectItem value="spouse">Spouse</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      
                      {showValidationErrors && !noAlternateContact && (!alternateContact || !alternateContactRelation) && (
                        <p className="text-xs text-red-600 mt-2">Please provide alternate contact number and relationship, or check "I don't have an alternate contact number"</p>
                      )}
                    </div>
                    
                    <div className="pt-3">
                      <Label className="text-sm font-medium mb-2 block">Upload Resume (PDF/DOC) *</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          id="resume-upload"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          disabled={isFormLocked() && !canEditSection('basic')}
                          onChange={(e) => handleResumeUpload(e.target.files[0])}
                        />
                        <label htmlFor="resume-upload">
                          <Button variant="outline" size="sm" asChild className={`cursor-pointer ${isFormLocked() && !canEditSection('basic') ? 'opacity-50 pointer-events-none' : ''}`}>
                            <span>
                              <Upload className="w-4 h-4 mr-2" />
                              {resumeFile ? 'Change Resume' : 'Choose File'}
                            </span>
                          </Button>
                        </label>
                        {resumeFile && (
                          <span className="text-sm text-emerald-600">✓ {resumeFile.name}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Auto-Filled Data Preview */}
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-3">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Auto-Filled Verification Data Preview
                    </CardTitle>
                    <p className="text-sm text-white/80">The following data will be automatically copied for various verification checks</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* World Database Check */}
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        World Database Check
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">Copies: Basic Details (Name, DOB, Father Name)</p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Name:</span>
                          <span className="ml-2 font-medium">{employee?.name || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">DOB:</span>
                          <span className="ml-2 font-medium">{employee?.dob || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Father's Name:</span>
                          <span className="ml-2 font-medium">{employee?.father_name || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Credit Check */}
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        Credit Check
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN Card</p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Name:</span>
                          <span className="ml-2 font-medium">{employee?.name || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">DOB:</span>
                          <span className="ml-2 font-medium">{employee?.dob || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">PAN Number:</span>
                          <span className="ml-2 font-medium">{panNumber || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* ID Check */}
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        ID Verification (PAN / Aadhar)
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN/Aadhar Number</p>
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Name:</span>
                          <span className="ml-2 font-medium">{employee?.name || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">DOB:</span>
                          <span className="ml-2 font-medium">{employee?.dob || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">PAN:</span>
                          <span className="ml-2 font-medium">{panNumber || '-'}</span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="text-slate-500">Aadhar:</span>
                          <span className="ml-2 font-medium">{aadharNumber || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Criminal Court & Advocate Check */}
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Criminal Court / Criminal Advocate Check
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">Copies: Present Address + Subsequent Addresses</p>
                      <div className="space-y-2 text-sm">
                        {presentAddress.complete_address && (
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Present Address:</span>
                            <span className="ml-2 font-medium">
                              {presentAddress.complete_address}, {presentAddress.city}, {presentAddress.state} - {presentAddress.pincode}
                            </span>
                          </div>
                        )}
                        {permanentAddress.complete_address && (
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Permanent Address:</span>
                            <span className="ml-2 font-medium">
                              {permanentAddress.complete_address}, {permanentAddress.city}, {permanentAddress.state} - {permanentAddress.pincode}
                            </span>
                          </div>
                        )}
                        {additionalAddresses.filter(a => a.complete_address).map((addr, idx) => (
                          <div key={idx} className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Additional Address {idx + 1} ({addr.address_type}):</span>
                            <span className="ml-2 font-medium">
                              {addr.complete_address}, {addr.city}, {addr.state} - {addr.pincode}
                            </span>
                          </div>
                        ))}
                        {!presentAddress.complete_address && !permanentAddress.complete_address && additionalAddresses.length === 0 && (
                          <div className="bg-amber-50 p-2 rounded text-amber-700">
                            No addresses filled yet. Please fill address details in the Address tab.
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </div> {/* End of basic section lock wrapper */}
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address" className="space-y-4">
                {/* Address Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('address')) && !canEditSection('address') && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${isSectionLockedByBackend('address') ? 'bg-blue-50 border border-blue-200' : 'bg-slate-100 border border-slate-300'}`}>
                    <div className={`flex items-center gap-2 ${isSectionLockedByBackend('address') ? 'text-blue-700' : 'text-slate-600'}`}>
                      <Lock className="w-5 h-5" />
                      <span>{isSectionLockedByBackend('address') 
                        ? 'This section has been approved by QC and is locked for verification.'
                        : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={(isFormLocked() || isSectionLockedByBackend('address')) && !canEditSection('address') ? 'opacity-60 pointer-events-none' : ''}>
                {/* Present Address */}
                <Card className={`border-slate-200 shadow-sm overflow-hidden ${showValidationErrors && (validationErrors.address.present.complete_address || validationErrors.address.present.city || validationErrors.address.present.state || validationErrors.address.present.pincode || validationErrors.address.present.address_proof) ? 'ring-2 ring-red-200' : ''}`}>
                  <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3">
                    <CardTitle className="text-white flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Present Address
                      </span>
                      {showValidationErrors && Object.keys(validationErrors.address.present).some(k => validationErrors.address.present[k]) && (
                        <Badge variant="outline" className="text-white border-white/50 bg-red-500/80">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Missing Required Fields
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Complete Address *</Label>
                        <Textarea
                          value={presentAddress.complete_address}
                          onChange={(e) => setPresentAddress({...presentAddress, complete_address: toTitleCase(e.target.value)})}
                          placeholder="Enter complete address with House No, Street, Area"
                          rows={3}
                          className={getErrorClass('address', 'present.complete_address')}
                        />
                        {showValidationErrors && validationErrors.address.present?.complete_address && (
                          <p className="text-xs text-red-600 mt-1">Complete address is required</p>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Landmark</Label>
                          <Input
                            placeholder="Landmark"
                            value={presentAddress.landmark}
                            onChange={(e) => setPresentAddress({...presentAddress, landmark: e.target.value})}
                          />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">State *</Label>
                      <div className={getErrorClass('address', 'present.state') ? 'rounded-md ' + getErrorClass('address', 'present.state') : ''}>
                        <Autocomplete
                          value={presentAddress.state}
                          onChange={(v) => setPresentAddress({...presentAddress, state: v, city: ''})}
                          suggestions={INDIAN_STATES}
                          placeholder="Select State"
                        />
                      </div>
                      {showValidationErrors && validationErrors.address.present?.state && (
                        <p className="text-xs text-red-600 mt-1">State is required</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">City *</Label>
                      <div className={getErrorClass('address', 'present.city') ? 'rounded-md ' + getErrorClass('address', 'present.city') : ''}>
                        <Autocomplete
                          value={presentAddress.city}
                          onChange={(v) => setPresentAddress({...presentAddress, city: toTitleCase(v)})}
                          suggestions={presentAddress.state ? getCitiesByState(presentAddress.state) : ALL_CITIES}
                          placeholder="Select City"
                        />
                      </div>
                      {showValidationErrors && validationErrors.address.present?.city && (
                        <p className="text-xs text-red-600 mt-1">City is required</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Pincode *</Label>
                      <Input
                        placeholder="Pincode"
                        value={presentAddress.pincode}
                        onChange={(e) => setPresentAddress({...presentAddress, pincode: e.target.value})}
                        className={getErrorClass('address', 'present.pincode')}
                      />
                      {showValidationErrors && validationErrors.address.present?.pincode && (
                        <p className="text-xs text-red-600 mt-1">Pincode is required</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Address Proof Upload */}
                  <div className={`mt-4 p-4 rounded-lg border ${showValidationErrors && !backendLockedSections.includes('address') && validationErrors.address.present?.address_proof ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200'}`}>
                    <Label className="font-medium">Address Proof{backendLockedSections.includes('address') ? '' : ' *'}</Label>
                    <p className="text-xs text-slate-500 mb-2">Upload a clear copy of your address proof (Utility Bill, Bank Statement, Rent Agreement, etc.)</p>
                    <div className="flex items-center gap-4">
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setPresentAddress({...presentAddress, address_proof: file});
                            try {
                              const result = await uploadFileToServer(file, 'present_address_proof', 'address_check');
                              if (result) {
                                setPresentAddress(prev => ({...prev, address_proof_url: result.file_url}));
                              }
                              toast.success('Present address proof uploaded');
                            } catch (error) {
                              toast.error('Failed to upload address proof');
                            }
                          }
                        }}
                        className={`flex-1 ${!presentAddress.address_proof && !presentAddress.address_proof_url ? 'border-amber-400' : ''}`}
                        data-testid="present-address-proof"
                      />
                      {(presentAddress.address_proof || presentAddress.address_proof_url) && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {presentAddress.address_proof ? presentAddress.address_proof.name : '✓ Uploaded'}
                        </span>
                      )}
                    </div>
                    {presentAddress.address_proof_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${presentAddress.address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                    )}
                    {!presentAddress.address_proof && !presentAddress.address_proof_url && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="present-address-proof-not-available"
                            checked={presentAddress.address_proof_reason === 'not_available'}
                            onChange={(e) => setPresentAddress({...presentAddress, address_proof_reason: e.target.checked ? 'not_available' : ''})}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <Label htmlFor="present-address-proof-not-available" className="text-sm font-normal cursor-pointer text-slate-600">
                            Address Proof Not Available
                          </Label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Permanent Address */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Permanent Address
                  {permanentSameAsPresent && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Same as Present</span>
                  )}
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleCopyAddress} className="border-white/50 bg-white/10 text-white hover:bg-white/20">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy from Present
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {permanentSameAsPresent && (
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                    <span>Permanent address copied from present. Only present address will be verified.</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => setPermanentSameAsPresent(false)}
                    >
                      Edit separately
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Complete Address *</Label>
                    <Textarea
                      value={permanentAddress.complete_address}
                      onChange={(e) => setPermanentAddress({...permanentAddress, complete_address: toTitleCase(e.target.value)})}
                      placeholder="Enter complete address"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Landmark</Label>
                      <Input
                        placeholder="Landmark"
                        value={permanentAddress.landmark}
                        onChange={(e) => setPermanentAddress({...permanentAddress, landmark: toTitleCase(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">State *</Label>
                      <Autocomplete
                        value={permanentAddress.state}
                        onChange={(v) => setPermanentAddress({...permanentAddress, state: v, city: ''})}
                        suggestions={INDIAN_STATES}
                        placeholder="Select State"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">City *</Label>
                      <Autocomplete
                        value={permanentAddress.city}
                        onChange={(v) => setPermanentAddress({...permanentAddress, city: toTitleCase(v)})}
                        suggestions={permanentAddress.state ? getCitiesByState(permanentAddress.state) : ALL_CITIES}
                        placeholder="Select City"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Pincode *</Label>
                      <Input
                        placeholder="Pincode"
                        value={permanentAddress.pincode}
                        onChange={(e) => setPermanentAddress({...permanentAddress, pincode: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  {/* Address Proof Upload */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Label className="font-medium">Address Proof{backendLockedSections.includes('address') ? '' : ' *'}</Label>
                    <p className="text-xs text-slate-500 mb-2">Upload a clear copy of your address proof (Utility Bill, Bank Statement, Rent Agreement, etc.)</p>
                    <div className="flex items-center gap-4">
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setPermanentAddress({...permanentAddress, address_proof: file});
                            try {
                              const result = await uploadFileToServer(file, 'permanent_address_proof', 'address_check');
                              if (result) {
                                setPermanentAddress(prev => ({...prev, address_proof_url: result.file_url}));
                              }
                              toast.success('Permanent address proof uploaded');
                            } catch (error) {
                              toast.error('Failed to upload address proof');
                            }
                          }
                        }}
                        className={`flex-1 ${!permanentAddress.address_proof && !permanentAddress.address_proof_url ? 'border-amber-400' : ''}`}
                        data-testid="permanent-address-proof"
                      />
                      {(permanentAddress.address_proof || permanentAddress.address_proof_url) && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {permanentAddress.address_proof ? permanentAddress.address_proof.name : '✓ Uploaded'}
                        </span>
                      )}
                    </div>
                    {permanentAddress.address_proof_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${permanentAddress.address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                    )}
                    {!permanentAddress.address_proof && !permanentAddress.address_proof_url && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="permanent-address-proof-not-available"
                            checked={permanentAddress.address_proof_reason === 'not_available'}
                            onChange={(e) => setPermanentAddress({...permanentAddress, address_proof_reason: e.target.checked ? 'not_available' : ''})}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <Label htmlFor="permanent-address-proof-not-available" className="text-sm font-normal cursor-pointer text-slate-600">
                            Address Proof Not Available
                          </Label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Addresses */}
            {additionalAddresses.length > 0 && (
              <div className="space-y-4">
                {additionalAddresses.map((addr, index) => (
                  <Card key={index} className="border-slate-200 shadow-sm overflow-hidden">
                    <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-slate-500 to-slate-600 text-white py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <CardTitle className="text-white text-base">Additional Address {index + 1}</CardTitle>
                        <select
                          value={addr.address_type}
                          onChange={(e) => updateAdditionalAddress(index, 'address_type', e.target.value)}
                          className="text-sm border border-white/30 bg-white/10 text-white rounded px-2 py-1"
                        >
                          <option value="previous" className="text-slate-800">Previous Address</option>
                          <option value="native" className="text-slate-800">Native Place</option>
                          <option value="office" className="text-slate-800">Office Address</option>
                          <option value="other" className="text-slate-800">Other</option>
                        </select>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAdditionalAddress(index)}
                        className="text-white hover:text-red-200 hover:bg-red-500/20"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-2">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <Label>Complete Address *</Label>
                          <Textarea
                            value={addr.complete_address}
                            onChange={(e) => updateAdditionalAddress(index, 'complete_address', toTitleCase(e.target.value))}
                            placeholder="Enter complete address with House No, Street, Area"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-slate-500">Landmark</Label>
                            <Input
                              placeholder="Landmark"
                              value={addr.landmark}
                              onChange={(e) => updateAdditionalAddress(index, 'landmark', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">State *</Label>
                            <Autocomplete
                              value={addr.state}
                              onChange={(v) => {
                                updateAdditionalAddress(index, 'state', v);
                                updateAdditionalAddress(index, 'city', '');
                              }}
                              suggestions={INDIAN_STATES}
                              placeholder="Select State"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">City *</Label>
                            <Autocomplete
                              value={addr.city}
                              onChange={(v) => updateAdditionalAddress(index, 'city', v)}
                              suggestions={addr.state ? getCitiesByState(addr.state) : ALL_CITIES}
                              placeholder="Select City"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Pincode *</Label>
                            <Input
                              placeholder="Pincode"
                              value={addr.pincode}
                              onChange={(e) => updateAdditionalAddress(index, 'pincode', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-slate-500">Stayed From</Label>
                            <Input
                              type="date"
                              value={addr.duration_from}
                              onChange={(e) => updateAdditionalAddress(index, 'duration_from', e.target.value)}
                            />
                            {addr.duration_from && <span className="text-xs text-slate-500">{formatDateForDisplay(addr.duration_from)}</span>}
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Stayed Till</Label>
                            <Input
                              type="date"
                              value={addr.duration_to}
                              onChange={(e) => updateAdditionalAddress(index, 'duration_to', e.target.value)}
                            />
                            {addr.duration_to && <span className="text-xs text-slate-500">{formatDateForDisplay(addr.duration_to)}</span>}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add Address Button */}
            <Button
              variant="outline"
              onClick={addAdditionalAddress}
              className="w-full border-dashed border-slate-300 hover:border-slate-400"
              data-testid="add-address-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Additional Address (Previous / Native Place / Other)
            </Button>
            </div> {/* End of address section lock wrapper */}
              </TabsContent>

              {/* ID Details Tab */}
              <TabsContent value="id" className="space-y-4">
                {/* ID Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('id')) && !canEditSection('id') && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${isSectionLockedByBackend('id') ? 'bg-blue-50 border border-blue-200' : 'bg-slate-100 border border-slate-300'}`}>
                    <div className={`flex items-center gap-2 ${isSectionLockedByBackend('id') ? 'text-blue-700' : 'text-slate-600'}`}>
                      <Lock className="w-5 h-5" />
                      <span>{isSectionLockedByBackend('id') 
                        ? 'This section has been approved by QC and is locked for verification.'
                        : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={(isFormLocked() || isSectionLockedByBackend('id')) && !canEditSection('id') ? 'opacity-60 pointer-events-none' : ''}>
            {/* ID Documents */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-3">
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    ID Documents
                  </span>
                  {showValidationErrors && (validationErrors.idDetails.panNumber || validationErrors.idDetails.aadharNumber) && (
                    <Badge variant="outline" className="text-white border-white/50 bg-red-500/80">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Missing Required Fields
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label>PAN Number *</Label>
                    <Input
                      value={panNumber}
                      onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                      placeholder="ABCDE1234F"
                      data-testid="pan-input"
                      className={getErrorClass('idDetails', 'panNumber')}
                    />
                    {showValidationErrors && validationErrors.idDetails?.panNumber && (
                      <p className="text-xs text-red-600">PAN Number is required</p>
                    )}
                    <div>
                      <Label className="text-sm mb-2 block">Upload PAN Card (PDF/JPG) *</Label>
                      <input
                        type="file"
                        id="pan-card-upload"
                        accept=".pdf,.jpg,.jpeg"
                        className="hidden"
                        onChange={(e) => handlePanCardUpload(e.target.files[0])}
                      />
                      <label htmlFor="pan-card-upload">
                        <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${panCardUrl ? 'bg-green-50 border-green-300' : ''}`}>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            {panCardFile ? panCardFile.name : (panCardUrl ? '✓ Uploaded' : 'Choose File')}
                          </span>
                        </Button>
                      </label>
                      {panCardUrl && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${panCardUrl}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block mt-1">View uploaded file</a>
                      )}
                      {panCardFile && !panCardUrl && (
                        <span className="text-xs text-emerald-600 block mt-1">✓ Uploading...</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Label>Aadhar Number *</Label>
                    <Input
                      value={aadharNumber}
                      onChange={(e) => setAadharNumber(e.target.value)}
                      placeholder="1234 5678 9012"
                      data-testid="aadhar-input"
                      className={getErrorClass('idDetails', 'aadharNumber')}
                    />
                    {showValidationErrors && validationErrors.idDetails?.aadharNumber && (
                      <p className="text-xs text-red-600">Aadhar Number is required</p>
                    )}
                    <div>
                      <Label className="text-sm mb-2 block">Upload Aadhar Card (PDF/JPG) *</Label>
                      <input
                        type="file"
                        id="aadhar-card-upload"
                        accept=".pdf,.jpg,.jpeg"
                        className="hidden"
                        onChange={(e) => handleAadharCardUpload(e.target.files[0])}
                      />
                      <label htmlFor="aadhar-card-upload">
                        <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${aadharCardUrl ? 'bg-green-50 border-green-300' : ''}`}>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            {aadharCardFile ? aadharCardFile.name : (aadharCardUrl ? '✓ Uploaded' : 'Choose File')}
                          </span>
                        </Button>
                      </label>
                      {aadharCardUrl && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${aadharCardUrl}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block mt-1">View uploaded file</a>
                      )}
                      {aadharCardFile && !aadharCardUrl && (
                        <span className="text-xs text-emerald-600 block mt-1">✓ Uploading...</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div> {/* End of ID section lock wrapper */}
              </TabsContent>

              {/* Employment Tab */}
              <TabsContent value="employment" className="space-y-4">
                {/* Employment Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('employment')) && !canEditSection('employment') && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${isSectionLockedByBackend('employment') ? 'bg-blue-50 border border-blue-200' : 'bg-slate-100 border border-slate-300'}`}>
                    <div className={`flex items-center gap-2 ${isSectionLockedByBackend('employment') ? 'text-blue-700' : 'text-slate-600'}`}>
                      <Lock className="w-5 h-5" />
                      <span>{isSectionLockedByBackend('employment') 
                        ? 'This section has been approved by QC and is locked for verification.'
                        : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={(isFormLocked() || isSectionLockedByBackend('employment')) && !canEditSection('employment') ? 'opacity-60 pointer-events-none' : ''}>
            {/* Employment History */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3">
                <div>
                  <CardTitle className="text-white flex items-center gap-3">
                    <Briefcase className="w-5 h-5" />
                    Employment History
                    {!isEmploymentRequired && (
                      <Badge className="bg-white/20 text-white border-white/30">
                        {employeeCategory.charAt(0).toUpperCase() + employeeCategory.slice(1)} - Employment Not Required
                      </Badge>
                    )}
                  </CardTitle>
                  {!isEmploymentRequired && (
                    <p className="text-sm text-white/80 mt-2">
                      As a {employeeCategory}, employment verification is not required. You can skip this section.
                    </p>
                  )}
                </div>
                {isEmploymentRequired && (
                  <Button variant="outline" size="sm" onClick={addEmploymentEntry} 
                    disabled={(() => { const req = getRequiredCountForCheckType('employment_check'); return req > 0 && employmentEntries.length >= req; })()}
                    className="border-white/50 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Employment {(() => { const req = getRequiredCountForCheckType('employment_check'); return req > 0 ? `(${employmentEntries.length}/${req})` : ''; })()}
                  </Button>
                )}
              </CardHeader>
              {isEmploymentRequired && (
                <CardContent className="space-y-6">
                  {employmentEntries.map((emp, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-6 relative">
                      {index > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEmploymentEntry(index)}
                          className="absolute top-2 right-2 text-rose-600 hover:text-rose-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      <h4 className="font-semibold text-lg mb-4">Employment {index + 1}</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <Label>Employment Type *</Label>
                          <select
                            value={emp.employment_type}
                            onChange={(e) => updateEmploymentEntry(index, 'employment_type', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-md"
                          >
                            <option value="current">Current</option>
                            <option value="past">Past</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Company Name *</Label>
                            <Autocomplete
                              value={emp.company_name}
                              onChange={(v) => {
                                updateEmploymentEntry(index, 'company_name', v);
                                fetchDatabaseSuggestions(v, 'company');
                              }}
                              suggestions={dbCompanySuggestions.length > 0 ? dbCompanySuggestions : INDIAN_COMPANIES}
                              placeholder="Type company name (suggestions from database)"
                              data-testid={`company-name-${index}`}
                              className={getErrorClass('employment', 'company_name', index)}
                            />
                          </div>
                          <div>
                            <Label>Employee ID</Label>
                            <Input
                              value={emp.employee_id}
                              onChange={(e) => updateEmploymentEntry(index, 'employee_id', e.target.value)}
                              placeholder="Employee ID"
                              data-testid={`employee-id-${index}`}
                              className={getErrorClass('employment', 'employee_id', index)}
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Location/Address *</Label>
                          <Input
                            value={emp.location_address}
                            onChange={(e) => updateEmploymentEntry(index, 'location_address', e.target.value)}
                            placeholder="Company location/address"
                            data-testid={`location-address-${index}`}
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Designation *</Label>
                            <Input
                              value={emp.designation}
                              onChange={(e) => updateEmploymentEntry(index, 'designation', toTitleCase(e.target.value))}
                              placeholder="Designation"
                              data-testid={`designation-${index}`}
                              className={getErrorClass('employment', 'designation', index)}
                            />
                          </div>
                          <div>
                            <Label>Employed From *</Label>
                            <Input
                              type="date"
                              value={emp.employed_from}
                              onChange={(e) => updateEmploymentEntry(index, 'employed_from', e.target.value)}
                              data-testid={`employed-from-${index}`}
                              className={getErrorClass('employment', 'employed_from', index)}
                            />
                            {emp.employed_from && <span className="text-xs text-slate-500">{formatDateForDisplay(emp.employed_from)}</span>}
                          </div>
                          <div>
                            <Label>Employed To {emp.employment_type === 'current' ? '(Present)' : '*'}</Label>
                            <Input
                              type="date"
                              value={emp.employed_to}
                              onChange={(e) => {
                                updateEmploymentEntry(index, 'employed_to', e.target.value);
                                if (emp.employed_from && e.target.value && new Date(e.target.value) < new Date(emp.employed_from)) {
                                  toast.error('Employed To date cannot be before Employed From date. Please check the dates.');
                                }
                              }}
                              disabled={emp.employment_type === 'current' && emp.can_verify_current === false}
                              data-testid={`employed-to-${index}`}
                              className={getErrorClass('employment', 'employed_to', index)}
                            />
                            {emp.employed_to && <span className="text-xs text-slate-500">{formatDateForDisplay(emp.employed_to)}</span>}
                            {emp.employed_from && emp.employed_to && new Date(emp.employed_to) < new Date(emp.employed_from) && (
                              <p className="text-xs text-red-500 mt-1">Employed To date is before Employed From date</p>
                            )}
                          </div>
                        </div>

                        {/* Current Employment Verification Toggle - Show right after Employed To for current employment */}
                        {emp.employment_type === 'current' && (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md space-y-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`can-verify-${index}`}
                                checked={emp.can_verify_current !== false}
                                onChange={(e) => updateEmploymentEntry(index, 'can_verify_current', e.target.checked)}
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`can-verify-current-${index}`}
                              />
                              <Label htmlFor={`can-verify-${index}`} className="text-sm font-normal cursor-pointer">
                                Verification from current employer is permitted
                              </Label>
                            </div>
                            {emp.can_verify_current === false && (
                              <div>
                                <Label className="text-sm">Date of Relieving *</Label>
                                <Input
                                  type="date"
                                  value={emp.date_of_relieving || ''}
                                  onChange={(e) => updateEmploymentEntry(index, 'date_of_relieving', e.target.value)}
                                  className={!emp.date_of_relieving ? 'border-amber-400' : ''}
                                  data-testid={`date-of-relieving-${index}`}
                                />
                                {emp.date_of_relieving && <span className="text-xs text-slate-500">{formatDateForDisplay(emp.date_of_relieving)}</span>}
                                {!emp.date_of_relieving && (
                                  <p className="text-xs text-amber-600 mt-1">Date of relieving is required when verification from current employer is not permitted</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Employment Gap Detection and Reason */}
                        {(() => {
                          const gap = getGapBeforeEntry(index);
                          if (gap) {
                            return (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-3">
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                                  <div>
                                    <p className="font-medium text-amber-800">Employment Gap Detected</p>
                                    <p className="text-sm text-amber-700">
                                      {gap.days} days gap between {gap.previousCompany} ({gap.fromDate}) and this employment ({gap.toDate})
                                    </p>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-amber-800">Reason for Gap *</Label>
                                  <select
                                    value={emp.gap_reason || ''}
                                    onChange={(e) => updateEmploymentEntry(index, 'gap_reason', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-md ${!emp.gap_reason ? 'border-amber-400' : 'border-slate-200'}`}
                                    data-testid={`gap-reason-${index}`}
                                  >
                                    <option value="">-- Select Reason --</option>
                                    {GAP_REASONS.map(reason => (
                                      <option key={reason} value={reason}>{reason}</option>
                                    ))}
                                  </select>
                                  {emp.gap_reason === 'Other' && (
                                    <div>
                                      <Input
                                        value={emp.gap_reason_other || ''}
                                        onChange={(e) => updateEmploymentEntry(index, 'gap_reason_other', e.target.value)}
                                        placeholder="Please specify the reason"
                                        className={!emp.gap_reason_other ? 'border-amber-400' : ''}
                                        data-testid={`gap-reason-other-${index}`}
                                      />
                                      {!emp.gap_reason_other && (
                                        <p className="text-xs text-amber-600 mt-1">Please specify the reason for the employment gap</p>
                                      )}
                                    </div>
                                  )}
                                  {!emp.gap_reason && (
                                    <p className="text-xs text-amber-600">Please select a reason for the employment gap</p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div>
                          <Label>Salary Per Month/Per Annum *</Label>
                          <Input
                            value={emp.salary_per_month}
                            onChange={(e) => updateEmploymentEntry(index, 'salary_per_month', e.target.value)}
                            placeholder="e.g., 50000 per month or 6 LPA"
                            className={getErrorClass('employment', 'salary_per_month', index) || (!emp.salary_per_month?.trim() ? 'border-amber-400 border-2' : '')}
                            data-testid={`salary-${index}`}
                          />
                          {!emp.salary_per_month?.trim() && (
                            <p className="text-xs text-amber-600 mt-1">Salary is required</p>
                          )}
                        </div>

                        {/* UAN/PF Number - Always visible, mandatory unless "Not Available" is checked */}
                        <div className="space-y-2">
                          <Label>UAN/PF Number {!emp.uan_pf_not_available ? '*' : ''}</Label>
                          <Input
                            value={emp.uan_pf_number}
                            onChange={(e) => updateEmploymentEntry(index, 'uan_pf_number', e.target.value)}
                            placeholder="Enter UAN/PF Number"
                            disabled={emp.uan_pf_not_available}
                            className={getErrorClass('employment', 'uan_pf_number', index) || (!emp.uan_pf_not_available && !emp.uan_pf_number ? 'border-amber-400' : '')}
                            data-testid={`uan-pf-number-${index}`}
                          />
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="checkbox"
                              id={`uan-not-available-${index}`}
                              checked={emp.uan_pf_not_available || false}
                              onChange={(e) => updateEmploymentEntry(index, 'uan_pf_not_available', e.target.checked)}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              data-testid={`uan-not-available-${index}`}
                            />
                            <Label htmlFor={`uan-not-available-${index}`} className="text-sm font-normal cursor-pointer text-slate-600">
                              UAN/PF Number Not Available
                            </Label>
                          </div>
                        </div>

                        <div className={`p-4 rounded-md space-y-4 ${isRmDetailsMandatory ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <h5 className="font-medium text-slate-900">Reporting Manager / References</h5>
                              {isRmDetailsMandatory && (
                                <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-100 text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Required for Professional Reference
                                </Badge>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => copyRMToReferences(index)}
                              className="text-xs"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy to References
                            </Button>
                          </div>
                          
                          {/* Reference 1 */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-700">Reference 1 {isRmDetailsMandatory ? '*' : ''}</Label>
                            <div className="grid grid-cols-3 gap-3">
                              <Input
                                placeholder={`Name ${isRmDetailsMandatory ? '*' : ''}`}
                                value={emp.rm_name}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_name', e.target.value)}
                                required={isRmDetailsMandatory}
                                className={getErrorClass('employment', 'rm_name', index) || (isRmDetailsMandatory && !emp.rm_name ? 'border-amber-400' : '')}
                                data-testid={`rm-name-${index}`}
                              />
                              <Input
                                placeholder={`Contact Number ${isRmDetailsMandatory ? '*' : ''}`}
                                value={emp.rm_contact_number}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_contact_number', formatPhoneMax10(e.target.value))}
                                maxLength={10}
                                required={isRmDetailsMandatory}
                                className={`${getErrorClass('employment', 'rm_contact_number', index) || (isRmDetailsMandatory && !emp.rm_contact_number ? 'border-amber-400' : '')} ${emp.rm_contact_number && emp.rm_contact_number.length !== 10 ? 'border-red-400' : ''}`}
                                data-testid={`rm-contact-${index}`}
                              />
                              {emp.rm_contact_number && emp.rm_contact_number.length !== 10 && (
                                <span className="text-xs text-red-500 col-span-3">Contact number must be 10 digits</span>
                              )}
                              <Input
                                placeholder={`Email ${isRmDetailsMandatory ? '*' : ''}`}
                                value={emp.rm_email}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_email', e.target.value)}
                                required={isRmDetailsMandatory}
                                className={getErrorClass('employment', 'rm_email', index) || (isRmDetailsMandatory && !emp.rm_email ? 'border-amber-400' : '')}
                                data-testid={`rm-email-${index}`}
                              />
                              <Input
                                placeholder={`Designation ${isRmDetailsMandatory ? '*' : ''}`}
                                value={emp.rm_designation}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_designation', toTitleCase(e.target.value))}
                                required={isRmDetailsMandatory}
                                className={getErrorClass('employment', 'rm_designation', index) || (isRmDetailsMandatory && !emp.rm_designation ? 'border-amber-400' : '')}
                                data-testid={`rm-designation-${index}`}
                              />
                              <Input
                                placeholder="Department"
                                value={emp.rm_department}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_department', toTitleCase(e.target.value))}
                                data-testid={`rm-department-${index}`}
                              />
                              <Input
                                placeholder="Company (if different)"
                                value={emp.rm_company}
                                onChange={(e) => updateEmploymentEntry(index, 'rm_company', toTitleCase(e.target.value))}
                                data-testid={`rm-company-${index}`}
                              />
                            </div>
                          </div>
                          
                          {/* Reference 2 */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-700">Reference 2 (Optional)</Label>
                            <div className="grid grid-cols-3 gap-3">
                              <Input
                                placeholder="Name"
                                value={emp.rm2_name}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_name', e.target.value)}
                                data-testid={`rm2-name-${index}`}
                              />
                              <Input
                                placeholder="Contact Number"
                                value={emp.rm2_contact_number}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_contact_number', formatPhoneMax10(e.target.value))}
                                maxLength={10}
                                className={emp.rm2_contact_number && emp.rm2_contact_number.length !== 10 ? 'border-red-400' : ''}
                                data-testid={`rm2-contact-${index}`}
                              />
                              {emp.rm2_contact_number && emp.rm2_contact_number.length !== 10 && (
                                <span className="text-xs text-red-500 col-span-3">Contact number must be 10 digits</span>
                              )}
                              <Input
                                placeholder="Email"
                                value={emp.rm2_email}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_email', e.target.value)}
                                data-testid={`rm2-email-${index}`}
                              />
                              <Input
                                placeholder="Designation"
                                value={emp.rm2_designation}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_designation', toTitleCase(e.target.value))}
                                data-testid={`rm2-designation-${index}`}
                              />
                              <Input
                                placeholder="Department"
                                value={emp.rm2_department}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_department', toTitleCase(e.target.value))}
                                data-testid={`rm2-department-${index}`}
                              />
                              <Input
                                placeholder="Company (if different)"
                                value={emp.rm2_company}
                                onChange={(e) => updateEmploymentEntry(index, 'rm2_company', toTitleCase(e.target.value))}
                                data-testid={`rm2-company-${index}`}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-4 rounded-md space-y-3">
                          <h5 className="font-medium text-slate-900">HR Details {!emp.hr_details_not_available && <span className="text-red-500">*</span>}</h5>
                          <div className="grid grid-cols-3 gap-3">
                            <Input
                              placeholder="HR Name"
                              value={emp.hr_name}
                              onChange={(e) => updateEmploymentEntry(index, 'hr_name', e.target.value)}
                              onBlur={() => formatEmploymentField(index, 'hr_name', emp.hr_name)}
                              disabled={emp.hr_details_not_available}
                              className={`${emp.hr_details_not_available ? 'opacity-50' : ''} ${getErrorClass('employment', 'hr_name', index)}`}
                              data-testid={`hr-name-${index}`}
                            />
                            <Input
                              placeholder="HR Email (must contain @)"
                              type="email"
                              value={emp.hr_email}
                              onChange={(e) => updateEmploymentEntry(index, 'hr_email', e.target.value.toLowerCase())}
                              onBlur={() => formatEmploymentField(index, 'hr_email', emp.hr_email)}
                              disabled={emp.hr_details_not_available}
                              className={`${emp.hr_details_not_available ? 'opacity-50' : ''} ${emp.hr_email && !isValidEmail(emp.hr_email) ? 'border-red-400' : ''} ${getErrorClass('employment', 'hr_email', index)}`}
                              data-testid={`hr-email-${index}`}
                            />
                            {emp.hr_email && !isValidEmail(emp.hr_email) && (
                              <p className="text-xs text-red-500">Invalid email - must contain @</p>
                            )}
                            <Input
                              placeholder="HR Contact Number (10 digits)"
                              value={emp.hr_contact_number}
                              onChange={(e) => updateEmploymentEntry(index, 'hr_contact_number', formatPhoneMax10(e.target.value))}
                              maxLength={10}
                              disabled={emp.hr_details_not_available}
                              className={`${emp.hr_details_not_available ? 'opacity-50' : ''} ${emp.hr_contact_number && emp.hr_contact_number.length !== 10 ? 'border-red-400' : ''} ${getErrorClass('employment', 'hr_contact_number', index)}`}
                              data-testid={`hr-contact-${index}`}
                            />
                            {emp.hr_contact_number && emp.hr_contact_number.length !== 10 && (
                              <p className="text-xs text-red-500">Phone must be 10 digits</p>
                            )}
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer mt-1" data-testid={`hr-not-available-${index}`}>
                            <input
                              type="checkbox"
                              checked={emp.hr_details_not_available || false}
                              onChange={(e) => updateEmploymentEntry(index, 'hr_details_not_available', e.target.checked)}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-600">I don't have HR details</span>
                          </label>
                        </div>

                        <div className="space-y-4 pt-3 border-t border-slate-200">
                          <h5 className="font-medium text-slate-900">Document Uploads *</h5>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-sm">Upload Relieving Letter (PDF/JPG) *</Label>
                              <input
                                type="file"
                                id={`relieving-${index}`}
                                accept=".pdf,.jpg,.jpeg"
                                className="hidden"
                                onChange={(e) => handleFileSelect('employment', index, 'relieving_letter', e.target.files[0])}
                              />
                              <label htmlFor={`relieving-${index}`}>
                                <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${emp.relieving_letter_url ? 'bg-green-50 border-green-300' : ''}`}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {emp.relieving_letter ? emp.relieving_letter.name : (emp.relieving_letter_url ? '✓ Uploaded' : 'Choose File')}
                                  </span>
                                </Button>
                              </label>
                              {emp.relieving_letter_url && (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}${emp.relieving_letter_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                              )}
                              {!emp.relieving_letter && !emp.relieving_letter_url && (
                                <Input
                                  placeholder="Reason if not available"
                                  value={emp.relieving_letter_reason}
                                  onChange={(e) => updateEmploymentEntry(index, 'relieving_letter_reason', e.target.value)}
                                  className="text-sm"
                                />
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Upload Latest Payslip (PDF/JPG) *</Label>
                              <input
                                type="file"
                                id={`payslip-${index}`}
                                accept=".pdf,.jpg,.jpeg"
                                className="hidden"
                                onChange={(e) => handleFileSelect('employment', index, 'latest_payslip', e.target.files[0])}
                              />
                              <label htmlFor={`payslip-${index}`}>
                                <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${emp.latest_payslip_url ? 'bg-green-50 border-green-300' : ''}`}>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {emp.latest_payslip ? emp.latest_payslip.name : (emp.latest_payslip_url ? '✓ Uploaded' : 'Choose File')}
                                  </span>
                                </Button>
                              </label>
                              {emp.latest_payslip_url && (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}${emp.latest_payslip_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                              )}
                              {!emp.latest_payslip && !emp.latest_payslip_url && (
                                <Input
                                  placeholder="Reason if not available"
                                  value={emp.latest_payslip_reason}
                                  onChange={(e) => updateEmploymentEntry(index, 'latest_payslip_reason', e.target.value)}
                                  className="text-sm"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
            </div> {/* End of employment section lock wrapper */}
              </TabsContent>

              {/* Education Tab */}
              <TabsContent value="education" className="space-y-4">
                {/* Education Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('education')) && !canEditSection('education') && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${isSectionLockedByBackend('education') ? 'bg-blue-50 border border-blue-200' : 'bg-slate-100 border border-slate-300'}`}>
                    <div className={`flex items-center gap-2 ${isSectionLockedByBackend('education') ? 'text-blue-700' : 'text-slate-600'}`}>
                      <Lock className="w-5 h-5" />
                      <span>{isSectionLockedByBackend('education') 
                        ? 'This section has been approved by QC and is locked for verification.'
                        : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={(isFormLocked() || isSectionLockedByBackend('education')) && !canEditSection('education') ? 'opacity-60 pointer-events-none' : ''}>
            {/* Education Details */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Education Details
                </CardTitle>
                <Button variant="outline" size="sm" onClick={addEducationEntry}
                  disabled={(() => { const req = getRequiredCountForCheckType('education_check'); return req > 0 && educationEntries.length >= req; })()}
                  className="border-white/50 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Education {(() => { const req = getRequiredCountForCheckType('education_check'); return req > 0 ? `(${educationEntries.length}/${req})` : ''; })()}
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {educationEntries.map((edu, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-6 relative">
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEducationEntry(index)}
                        className="absolute top-2 right-2 text-rose-600 hover:text-rose-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <h4 className="font-semibold text-lg mb-4">
                      Education {index + 1}
                      {(() => {
                        // Show package requirement hint from case checks
                        const eduChecks = (verificationCase?.checks || []).filter(c => c.check_type === 'education_check');
                        const matchingCheck = eduChecks[index];
                        if (matchingCheck?.sub_option) {
                          const SUB_OPTION_LABELS = {
                            'highest_qualification': 'Highest Qualification (e.g., Graduation/PG)',
                            'prior_to_highest_ssc': 'SSC/10th',
                            'prior_ssc': 'SSC/10th',
                            'prior_to_highest_hsc': 'HSC/12th/PUC',
                            'prior_hsc': 'HSC/12th/PUC',
                            'prior_to_highest': 'SSC/10th or HSC/12th',
                            'iti': 'ITI',
                            'diploma': 'Diploma',
                            'professional_course': 'Professional Course'
                          };
                          let label = SUB_OPTION_LABELS[matchingCheck.sub_option] || matchingCheck.sub_option.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          // Remove "Prior" prefix if present and append /10th to SSC
                          label = label.replace(/^Prior\s*(To\s*Highest\s*)?/i, '');
                          if (label.toLowerCase() === 'ssc') label = 'SSC/10th';
                          if (label.toLowerCase() === 'hsc') label = 'HSC/12th/PUC';
                          return (
                            <span className="ml-2 text-sm font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-md border border-red-300">
                              Update {label} as per client requirement
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </h4>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Education Type *</Label>
                          <select
                            value={edu.education_type}
                            onChange={(e) => updateEducationEntry(index, 'education_type', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-md"
                          >
                            <option value="highest">Highest</option>
                            <option value="prior">Prior to Highest</option>
                          </select>
                        </div>
                        {/* Mandatory candidate-side acknowledgment of which
                            specific certificate they are about to upload.
                            Pure UI — this value is NOT wired to backend
                            check-mapping (which uses check_index → sub_option)
                            and does not affect any downstream flow. Purpose:
                            stop 12th being uploaded when the package asks for
                            10th, etc. (Mar 2, 2026). */}
                        <div>
                          <Label>
                            Education Checks (as per package) <span className="text-rose-600">*</span>
                          </Label>
                          <select
                            value={edu.acknowledged_cert_type || ''}
                            onChange={(e) => updateEducationEntry(index, 'acknowledged_cert_type', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md ${edu.acknowledged_cert_type ? 'border-slate-200' : 'border-rose-300 bg-rose-50'}`}
                            data-testid={`edu-ack-cert-type-${index}`}
                            required
                          >
                            <option value="">-- Select the certificate you are uploading --</option>
                            {(() => {
                              // Options are the LITERAL package education sub-checks
                              // for this case, so candidate cannot pick an
                              // off-package cert type. Source: `packageChecks`
                              // (from the fetched package doc, contains
                              // sub_options) with a fallback to per-check
                              // entries on `verificationCase.checks` for
                              // legacy custom_checks payloads.
                              const CERT_LABELS = {
                                'highest_qualification': 'Highest — Graduation / PG',
                                'highest': 'Highest — Graduation / PG',
                                'prior_ssc': 'SSC — 10th / SSLC',
                                'prior_to_highest_ssc': 'SSC — 10th / SSLC',
                                'prior_hsc': 'HSC — 12th / PUC',
                                'prior_to_highest_hsc': 'HSC — 12th / PUC',
                                'prior_to_highest': 'Prior to Highest',
                                'iti': 'ITI',
                                'diploma': 'Diploma',
                                'graduation': 'Graduation',
                                'post_graduation': 'Post Graduation',
                                'professional_course': 'Professional Course',
                                'ssc': 'SSC — 10th / SSLC',
                                'hsc': 'HSC — 12th / PUC',
                                'class_10': 'SSC — 10th / SSLC',
                                'class_12': 'HSC — 12th / PUC',
                                'puc': 'HSC — 12th / PUC',
                                'sslc': 'SSC — 10th / SSLC',
                                'phd': 'PhD',
                              };
                              // Collect sub_options from package.
                              const eduSubOptions = [];
                              const seen = new Set();
                              const pushOpt = (key) => {
                                if (key && !seen.has(key)) {
                                  seen.add(key);
                                  eduSubOptions.push(key);
                                }
                              };
                              (packageChecks || []).forEach(pc => {
                                const ct = typeof pc === 'string' ? pc : (pc?.check_type || pc?.type);
                                if (ct !== 'education_check') return;
                                const subs = (pc?.sub_options || []);
                                if (subs.length > 0) {
                                  subs.forEach(pushOpt);
                                } else {
                                  // package check with qty>1 and no sub_options
                                  // → fall back to canonical defaults so the
                                  // dropdown isn't empty.
                                  const qty = pc?.quantity || 1;
                                  const defaults = ['highest', 'prior_ssc', 'prior_hsc', 'graduation', 'post_graduation'];
                                  defaults.slice(0, qty).forEach(pushOpt);
                                }
                              });
                              // Fallback to case-level checks if package returned nothing.
                              if (eduSubOptions.length === 0) {
                                (verificationCase?.checks || [])
                                  .filter(c => c.check_type === 'education_check')
                                  .forEach(c => pushOpt(c.sub_option));
                              }
                              return eduSubOptions.map(key => (
                                <option key={key} value={key}>
                                  {CERT_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, x => x.toUpperCase())}
                                </option>
                              ));
                            })()}
                          </select>
                          <p className="text-xs text-orange-700 mt-1" data-testid={`edu-ack-note-${index}`}>
                            As per <strong>{clientName || verificationCase?.client_name || verificationCase?.entity_name || 'Client'}</strong>, upload only the requested Education documents.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name as Per Document *</Label>
                          <Input
                            value={edu.name_as_per_document}
                            onChange={(e) => updateEducationEntry(index, 'name_as_per_document', e.target.value)}
                            placeholder="Name as per certificate"
                          />
                        </div>
                        <div>
                          <Label>University Name *</Label>
                          <Autocomplete
                            value={edu.university_name}
                            onChange={(v) => {
                              updateEducationEntry(index, 'university_name', v);
                              fetchDatabaseSuggestions(v, 'institution');
                            }}
                            suggestions={dbInstitutionSuggestions.length > 0 ? dbInstitutionSuggestions : INDIAN_UNIVERSITIES}
                            placeholder="Type university/board name (suggestions from database)"
                            data-testid={`university-name-${index}`}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>College *</Label>
                          <Autocomplete
                            value={edu.college}
                            onChange={(v) => {
                              updateEducationEntry(index, 'college', v);
                              fetchDatabaseSuggestions(v, 'institution');
                            }}
                            suggestions={dbInstitutionSuggestions.length > 0 ? dbInstitutionSuggestions : INDIAN_COLLEGES}
                            placeholder="Type college/institution name"
                            data-testid={`college-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label>Course *</Label>
                          <Input
                            value={edu.course}
                            onChange={(e) => updateEducationEntry(index, 'course', e.target.value)}
                            placeholder="e.g., B.Tech, MBA, B.Sc"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Specialization</Label>
                          <Input
                            value={edu.specialization}
                            onChange={(e) => updateEducationEntry(index, 'specialization', toTitleCase(e.target.value))}
                            placeholder="e.g., Computer Science"
                          />
                        </div>
                        <div>
                          <Label>Year of Passing *</Label>
                          <Input
                            value={edu.year_of_passing}
                            onChange={(e) => updateEducationEntry(index, 'year_of_passing', e.target.value)}
                            placeholder="e.g., 2020"
                          />
                        </div>
                        <div>
                          <Label>Roll No/Regd No</Label>
                          <Input
                            value={edu.roll_no}
                            onChange={(e) => updateEducationEntry(index, 'roll_no', e.target.value)}
                            placeholder="Roll/Registration number"
                          />
                        </div>
                      </div>

                      <div className="space-y-4 pt-3 border-t border-slate-200">
                        <h5 className="font-medium text-slate-900">Document Uploads *</h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm">Upload Degree Certificate (PDF/JPG) *</Label>
                            <input
                              type="file"
                              id={`degree-${index}`}
                              accept=".pdf,.jpg,.jpeg"
                              className="hidden"
                              onChange={(e) => handleFileSelect('education', index, 'degree_certificate', e.target.files[0])}
                            />
                            <label htmlFor={`degree-${index}`}>
                              <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${edu.degree_certificate_url ? 'bg-green-50 border-green-300' : ''}`}>
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {edu.degree_certificate ? edu.degree_certificate.name : (edu.degree_certificate_url ? '✓ Uploaded' : 'Choose File')}
                                </span>
                              </Button>
                            </label>
                            {edu.degree_certificate_url && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${edu.degree_certificate_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                            )}
                            {!edu.degree_certificate && !edu.degree_certificate_url && (
                              <Input
                                placeholder="Reason if not available"
                                value={edu.degree_certificate_reason}
                                onChange={(e) => updateEducationEntry(index, 'degree_certificate_reason', e.target.value)}
                                className="text-sm"
                              />
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Upload Consolidated Marks Card (PDF/JPEG) *</Label>
                            <input
                              type="file"
                              id={`marks-${index}`}
                              accept=".pdf,.jpg,.jpeg"
                              className="hidden"
                              onChange={(e) => handleFileSelect('education', index, 'marks_card', e.target.files[0])}
                            />
                            <label htmlFor={`marks-${index}`}>
                              <Button variant="outline" size="sm" asChild className={`cursor-pointer w-full ${edu.marks_card_url ? 'bg-green-50 border-green-300' : ''}`}>
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {edu.marks_card ? edu.marks_card.name : (edu.marks_card_url ? '✓ Uploaded' : 'Choose File')}
                                </span>
                              </Button>
                            </label>
                            {edu.marks_card_url && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${edu.marks_card_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View uploaded file</a>
                            )}
                            {!edu.marks_card && !edu.marks_card_url && (
                              <Input
                                placeholder="Reason if not available"
                                value={edu.marks_card_reason}
                                onChange={(e) => updateEducationEntry(index, 'marks_card_reason', e.target.value)}
                                className="text-sm"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            </div> {/* End of education section lock wrapper */}
              </TabsContent>

              {/* References Tab (Optional) */}
              <TabsContent value="references" className="space-y-4">
                {/* References Section Lock Warning */}
                {(isFormLocked() || isSectionLockedByBackend('references')) && !canEditSection('references') && (
                  <div className={`rounded-lg p-4 flex items-center justify-between ${isSectionLockedByBackend('references') ? 'bg-blue-50 border border-blue-200' : 'bg-slate-100 border border-slate-300'}`}>
                    <div className={`flex items-center gap-2 ${isSectionLockedByBackend('references') ? 'text-blue-700' : 'text-slate-600'}`}>
                      <Lock className="w-5 h-5" />
                      <span>{isSectionLockedByBackend('references') 
                        ? 'This section has been approved by QC and is locked for verification.'
                        : 'This section is locked. You can only edit when an insufficiency is raised.'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className={(isFormLocked() || isSectionLockedByBackend('references')) && !canEditSection('references') ? 'opacity-60 pointer-events-none' : ''}>
            {/* References */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-rose-600 to-rose-700 text-white py-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  References 
                  {(hasPersonalRefCheck || hasProfessionalRefCheck) ? (
                    <Badge className="bg-white/20 text-white border-white/30">Required by Package</Badge>
                  ) : (
                    <span className="text-sm font-normal text-white/70">(Optional)</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {(hasPersonalRefCheck || hasProfessionalRefCheck) && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                    <p className="text-sm text-amber-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Your verification package requires reference checks. Please provide the details below.
                    </p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    Personal References
                    {hasPersonalRefCheck ? (
                      <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                    ) : (
                      <span className="text-sm font-normal text-slate-500">(Optional)</span>
                    )}
                  </h4>
                  {hasPersonalRefCheck && showValidationErrors && validationErrors.personalRef?.[0]?.name && (
                    <p className="text-red-500 text-sm mb-2">Please provide at least one personal reference</p>
                  )}
                  {personalRefs.map((ref, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-500 w-6">{idx + 1}.</span>
                      <Input
                        placeholder="Name *"
                        value={ref.name}
                        className={`flex-1 ${getErrorClass('personalRef', 'name', idx)}`}
                        onChange={(e) => {
                          const newRefs = [...personalRefs];
                          newRefs[idx].name = e.target.value;
                          setPersonalRefs(newRefs);
                        }}
                        onBlur={() => formatReferenceField('personal', idx, 'name', ref.name)}
                      />
                      <Input
                        placeholder="Phone * (10 digits)"
                        value={ref.phone}
                        maxLength={10}
                        className={`flex-1 ${getErrorClass('personalRef', 'phone', idx)} ${ref.phone && ref.phone.length !== 10 ? 'border-red-400' : ''}`}
                        onChange={(e) => {
                          const newRefs = [...personalRefs];
                          newRefs[idx].phone = formatPhoneMax10(e.target.value);
                          setPersonalRefs(newRefs);
                        }}
                      />
                      <Input
                        placeholder="Relation"
                        value={ref.relation}
                        className="flex-1"
                        onChange={(e) => {
                          const newRefs = [...personalRefs];
                          newRefs[idx].relation = e.target.value;
                          setPersonalRefs(newRefs);
                        }}
                        onBlur={() => formatReferenceField('personal', idx, 'relation', ref.relation)}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    Professional References
                    {hasProfessionalRefCheck ? (
                      <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                    ) : (
                      <span className="text-sm font-normal text-slate-500">(Optional)</span>
                    )}
                  </h4>
                  <p className="text-xs text-slate-500 mb-3">You can use "Copy to References" button in Employment section to auto-fill RM details</p>
                  {hasProfessionalRefCheck && showValidationErrors && validationErrors.professionalRef?.[0]?.name && (
                    <p className="text-red-500 text-sm mb-2">Please provide at least one professional reference</p>
                  )}
                  <div className="space-y-3">
                    {professionalRefs.map((ref, idx) => {
                      // Only show validation when: section is mandatory OR user has started filling this entry
                      const showValidation = hasProfessionalRefCheck || ref.name?.trim();
                      const reqMark = hasProfessionalRefCheck ? ' *' : '';
                      return (
                      <div key={idx} className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 w-6">{idx + 1}.</span>
                        <Input
                          placeholder={`Name${reqMark}`}
                          value={ref.name}
                          className={`flex-1 min-w-[120px] ${showValidation ? getErrorClass('professionalRef', 'name', idx) : ''}`}
                          onChange={(e) => {
                            const newRefs = [...professionalRefs];
                            newRefs[idx].name = e.target.value;
                            setProfessionalRefs(newRefs);
                          }}
                          onBlur={() => formatReferenceField('professional', idx, 'name', ref.name)}
                        />
                        <Input
                          placeholder={`Phone${reqMark} (10 digits)`}
                          value={ref.phone}
                          maxLength={10}
                          className={`flex-1 min-w-[120px] ${showValidation ? getErrorClass('professionalRef', 'phone', idx) : ''} ${showValidation && ref.phone && ref.phone.length !== 10 ? 'border-red-400' : ''}`}
                          onChange={(e) => {
                            const newRefs = [...professionalRefs];
                            newRefs[idx].phone = formatPhoneMax10(e.target.value);
                            setProfessionalRefs(newRefs);
                          }}
                        />
                        <Input
                          placeholder={`Designation${reqMark}`}
                          value={ref.designation}
                          className={`flex-1 min-w-[100px] ${showValidation ? getErrorClass('professionalRef', 'designation', idx) : ''}`}
                          onChange={(e) => {
                            const newRefs = [...professionalRefs];
                            newRefs[idx].designation = e.target.value;
                            setProfessionalRefs(newRefs);
                          }}
                          onBlur={() => formatReferenceField('professional', idx, 'designation', ref.designation)}
                        />
                        <Input
                          placeholder="Company"
                          value={ref.company}
                          className="flex-1 min-w-[100px]"
                          onChange={(e) => {
                            const newRefs = [...professionalRefs];
                            newRefs[idx].company = e.target.value;
                            setProfessionalRefs(newRefs);
                          }}
                          onBlur={() => formatReferenceField('professional', idx, 'company', ref.company)}
                        />
                        <Input
                          placeholder={`Email${reqMark}`}
                          type="email"
                          value={ref.email}
                          className={`flex-1 min-w-[150px] ${showValidation ? getErrorClass('professionalRef', 'email', idx) : ''} ${showValidation && ref.email && !isValidEmail(ref.email) ? 'border-red-400' : ''}`}
                          onChange={(e) => {
                            const newRefs = [...professionalRefs];
                            newRefs[idx].email = e.target.value.toLowerCase();
                            setProfessionalRefs(newRefs);
                          }}
                        />
                        {showValidation && ref.email && !isValidEmail(ref.email) && (
                          <p className="text-xs text-red-500 w-full ml-6">Invalid email format</p>
                        )}
                      </div>
                    )})}
                  </div>
                </div>
              </CardContent>
            </Card>
            </div> {/* End of references section lock wrapper */}
              </TabsContent>

              {/* Review & Submit Tab */}
              <TabsContent value="review" className="space-y-4">
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Review & Submit
                    </CardTitle>
                    <p className="text-sm text-white/80">Review your verification requirements and submit your details</p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Package Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Your Verification Package: {packageName || (verificationCase ? 'Custom Package' : 'Loading...')}</h4>
                      <p className="text-sm text-blue-700">The following checks will be performed based on your verification requirements</p>
                    </div>

                    {/* Verification Checks Required */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-900">Verification Checks Required</h4>
                      
                      {/* Always use packageChecks for proper quantity display */}
                      {(() => {
                        // Use packageChecks for quantities, which has the correct structure
                        const checksToDisplay = packageChecks;
                        
                        if (!checksToDisplay || checksToDisplay.length === 0) {
                          return (
                            <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500">
                              No verification checks configured for this case
                            </div>
                          );
                        }
                        
                        // Group checks by type and sum quantities
                        const groupedChecks = {};
                        checksToDisplay.forEach(check => {
                          let checkType = 'unknown';
                          let quantity = 1;
                          
                          if (typeof check === 'string') {
                            checkType = check;
                          } else if (typeof check === 'object') {
                            checkType = check.check_type || check.type || 'unknown';
                            quantity = check.quantity || 1;
                          }
                          
                          // Normalize check types - merge similar types
                          if (checkType === 'court_advocate_check') {
                            checkType = 'criminal_advocate_check';
                          }
                          
                          if (groupedChecks[checkType]) {
                            groupedChecks[checkType].quantity += quantity;
                          } else {
                            groupedChecks[checkType] = { checkType, quantity };
                          }
                        });
                        
                        const uniqueChecks = Object.values(groupedChecks);
                        
                        return (
                          <div className="grid gap-3">
                            {uniqueChecks.map((checkData, idx) => {
                              const checkType = checkData.checkType;
                              const quantity = checkData.quantity;
                              
                              // Map check types to user-friendly names and icons
                              const checkInfo = {
                                'id_check': { name: 'ID Verification', icon: CreditCard, section: 'id' },
                                'employment_check': { name: 'Employment Verification', icon: Briefcase, section: 'employment' },
                                'education_check': { name: 'Education Verification', icon: GraduationCap, section: 'education' },
                                'address_check': { name: 'Address Verification', icon: MapPin, section: 'address' },
                                'professional_reference': { name: 'Professional Reference Check', icon: Users, section: 'references' },
                                'personal_reference': { name: 'Personal Reference Check', icon: Users, section: 'references' },
                                'reference_check': { name: 'Reference Check', icon: Users, section: 'references' },
                                'criminal_court_check': { name: 'Criminal Court Check', icon: FileText, section: 'address' },
                                'court_advocate_check': { name: 'Court Advocate Check', icon: FileText, section: 'address' },
                                'criminal_advocate_check': { name: 'Criminal Advocate Check', icon: FileText, section: 'address' },
                                'world_criminal_db': { name: 'World Database Check', icon: FileText, section: 'basic' },
                                'india_criminal_db': { name: 'India Criminal Database Check', icon: FileText, section: 'basic' },
                                'global_database': { name: 'Global Database Check', icon: FileText, section: 'basic' },
                                'credit_check': { name: 'Credit Check', icon: CreditCard, section: 'id' },
                                'drug_test': { name: 'Drug Test', icon: FileText, section: 'basic' }
                              };
                              
                              const checkTypeStr = String(checkType || 'Unknown');
                              const info = checkInfo[checkTypeStr] || { name: checkTypeStr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), icon: FileText, section: null };
                              const Icon = info.icon;
                              
                              // Count filled entries
                              let filledCount = 0;
                              let requiredCount = quantity;
                              let canDeclare = false;
                              let declaration = false;
                              let setDeclaration = null;
                              let declarationText = '';
                              let autoFilledSource = ''; // Source of auto-filled data
                              
                              if (checkTypeStr === 'employment_check') {
                                filledCount = employmentEntries.filter(e => e.company_name?.trim()).length;
                                canDeclare = filledCount > 0 && filledCount < requiredCount;
                                declaration = employmentDeclaration;
                                setDeclaration = setEmploymentDeclaration;
                                declarationText = `I declare that I have only ${filledCount} employment record(s) to verify`;
                              } else if (checkTypeStr === 'education_check') {
                                filledCount = educationEntries.filter(e => e.course?.trim()).length;
                                canDeclare = filledCount > 0 && filledCount < requiredCount;
                                declaration = educationDeclaration;
                                setDeclaration = setEducationDeclaration;
                                declarationText = `I declare that I have only ${filledCount} education record(s) to verify`;
                              } else if (checkTypeStr === 'address_check') {
                                const additionalFilledCount = additionalAddresses.filter(a => a.complete_address?.trim()).length;
                                filledCount = (presentAddress.complete_address ? 1 : 0) + (permanentAddress.complete_address ? 1 : 0) + additionalFilledCount;
                                canDeclare = filledCount > 0 && filledCount < requiredCount;
                                declaration = addressDeclaration;
                                setDeclaration = setAddressDeclaration;
                                declarationText = `I declare that I have only ${filledCount} address(es) to verify`;
                              } else if (checkTypeStr === 'professional_reference') {
                                filledCount = professionalRefs.filter(r => r.name?.trim()).length;
                              } else if (checkTypeStr === 'personal_reference' || checkTypeStr === 'reference_check') {
                                // For reference_check or personal_reference, count filled personal references
                                filledCount = personalRefs.filter(r => r.name?.trim()).length;
                              } else if (checkTypeStr === 'id_check') {
                                filledCount = (panNumber ? 1 : 0) + (aadharNumber ? 1 : 0);
                                autoFilledSource = 'Copies: Basic Details + PAN/Aadhar Number';
                              } else if (checkTypeStr === 'criminal_court_check' || checkTypeStr === 'criminal_advocate_check') {
                                // Criminal checks copy from Present Address + subsequent addresses
                                const totalAddresses = (presentAddress.complete_address ? 1 : 0) + 
                                                       (permanentAddress.complete_address ? 1 : 0) + 
                                                       additionalAddresses.filter(a => a.complete_address.trim() !== '').length;
                                filledCount = presentAddress.complete_address ? Math.min(requiredCount, totalAddresses) : 0;
                                autoFilledSource = 'Copies: Present Address + Subsequent Addresses';
                              } else if (checkTypeStr === 'world_criminal_db') {
                                // World Database copies Basic Details (name, DOB, father name)
                                filledCount = (employee?.name && employee?.dob && employee?.father_name) ? requiredCount : 0;
                                autoFilledSource = 'Copies: Basic Details (Name, DOB, Father Name)';
                              } else if (checkTypeStr === 'india_criminal_db') {
                                // India Criminal Database copies Basic Details (name, DOB, father name)
                                filledCount = (employee?.name && employee?.dob && employee?.father_name) ? requiredCount : 0;
                                autoFilledSource = 'Copies: Basic Details (Name, DOB, Father Name)';
                              } else if (checkTypeStr === 'global_database') {
                                // Global Database copies Basic Details (name, DOB, father name)
                                filledCount = (employee?.name && employee?.dob && employee?.father_name) ? requiredCount : 0;
                                autoFilledSource = 'Copies: Basic Details (Name, DOB, Father Name)';
                              } else if (checkTypeStr === 'credit_check') {
                                // Credit Check copies Basic Details + PAN
                                filledCount = (employee?.name && panNumber) ? requiredCount : 0;
                                autoFilledSource = 'Copies: Basic Details + PAN Card';
                              } else if (checkTypeStr === 'drug_test') {
                                // Drug test - just needs basic details
                                filledCount = employee?.name ? requiredCount : 0;
                                autoFilledSource = 'Copies: Basic Details';
                              }
                          
                          const isComplete = filledCount >= requiredCount || (canDeclare && declaration);
                          // PACKAGE QUANTITY GUARD (visual cue): block on extras
                          const hasExtras = (
                            (checkTypeStr === 'employment_check' || checkTypeStr === 'education_check') &&
                            requiredCount > 0 && filledCount > requiredCount
                          );
                          const cardClass = hasExtras
                            ? 'bg-red-50 border-red-300'
                            : (isComplete ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200');
                          const iconClass = hasExtras
                            ? 'text-red-600'
                            : (isComplete ? 'text-emerald-600' : 'text-amber-600');
                          const titleClass = hasExtras
                            ? 'text-red-900'
                            : (isComplete ? 'text-emerald-900' : 'text-amber-900');
                          const subClass = hasExtras
                            ? 'text-red-700'
                            : (isComplete ? 'text-emerald-700' : 'text-amber-700');
                          
                          return (
                            <div key={idx} className={`p-4 rounded-lg border ${cardClass}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Icon className={`w-5 h-5 ${iconClass}`} />
                                  <div>
                                    <p className={`font-medium ${titleClass}`}>
                                      {info.name}
                                    </p>
                                    <p className={`text-sm ${subClass}`}>
                                      Required: {requiredCount} | Filled: {filledCount}
                                      {autoFilledSource && <span className="ml-2 text-xs italic">({autoFilledSource})</span>}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {hasExtras ? (
                                    <Badge className="bg-red-600 text-white" data-testid={`review-${checkTypeStr}-extras-badge`}>
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Remove Extras
                                    </Badge>
                                  ) : isComplete ? (
                                    <Badge className="bg-emerald-600 text-white">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Complete
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-amber-600 text-white">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Incomplete
                                    </Badge>
                                  )}
                                  {info.section && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setDetailsTab(info.section)}
                                      className="text-xs"
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              {/* Declaration option if fewer entries than required */}
                              {canDeclare && setDeclaration && (
                                <div className="mt-3 pt-3 border-t border-amber-300">
                                  <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={declaration}
                                      onChange={(e) => setDeclaration(e.target.checked)}
                                      className="mt-1 w-4 h-4 rounded border-amber-400"
                                    />
                                    <span className="text-sm text-amber-800">
                                      {declarationText}. I understand that fewer records will be verified.
                                    </span>
                                  </label>
                                </div>
                              )}

                              {/* PACKAGE QUANTITY GUARD warning */}
                              {hasExtras && (
                                <div className="mt-3 pt-3 border-t border-red-300" data-testid={`review-${checkTypeStr}-extras-warning`}>
                                  <p className="text-sm text-red-800">
                                    <strong>You have {filledCount - requiredCount} extra {checkTypeStr === 'employment_check' ? 'employment' : 'education'} {filledCount - requiredCount === 1 ? 'entry' : 'entries'}.</strong>{' '}
                                    Your verification package allows only <strong>{requiredCount}</strong>. Please go to the {checkTypeStr === 'employment_check' ? 'Employment' : 'Education'} tab and remove the extras before submitting.
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                        );
                      })()}
                    </div>

                    {/* Final Declaration with Signature */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-6">
                      <h4 className="font-semibold text-slate-900 mb-3">Final Declaration</h4>
                      
                      {/* Declaration Text */}
                      <div className="mb-4 p-4 bg-white border border-slate-200 rounded-lg">
                        {declarationText ? (
                          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{declarationText}</p>
                        ) : (
                          <p className="text-sm text-slate-500 italic">Loading declaration...</p>
                        )}
                      </div>
                      
                      {/* Declaration Checkbox */}
                      <div className="mb-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={signatureConfirmed && signatureData}
                            onChange={() => {}}
                            disabled={!signatureData}
                            className="mt-1 w-5 h-5 rounded border-slate-300"
                            data-testid="declaration-checkbox"
                          />
                          <span className="text-sm text-slate-700">
                            I have read and agree to the above declaration. I confirm that all the information provided is true and accurate to the best of my knowledge.
                          </span>
                        </label>
                      </div>
                      
                      {declarationSigned ? (
                        <div className="flex items-center gap-2 text-emerald-600 p-4 bg-emerald-50 rounded-lg">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Declaration signed and submitted successfully</span>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Sign instruction message */}
                          {(!signatureConfirmed || !signatureData) && canSubmit() && (
                            <p className="text-sm text-amber-600 text-center bg-amber-50 p-2 rounded-lg border border-amber-200">
                              Please sign below and click "Confirm Signature" to enable submission
                            </p>
                          )}
                          
                          {/* Signature Pad */}
                          <div className="border border-slate-200 rounded-lg p-4 bg-white">
                            <h5 className="font-medium text-slate-800 mb-3">Your Signature</h5>
                            <SignaturePad 
                              onSign={(data) => {
                                setSignatureData(data);
                                setSignatureConfirmed(true);
                                toast.success('Signature captured successfully');
                              }}
                              onClear={() => {
                                setSignatureData(null);
                                setSignatureConfirmed(false);
                              }}
                              disabled={declarationSigned}
                            />
                          </div>
                          
                          {/* Submit Button */}
                          <Button
                            onClick={handleFinalSubmit}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                            disabled={!canSubmit() || !signatureConfirmed || !signatureData}
                            data-testid="final-submit-button"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Review Complete - Submit for Verification
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Submission Status */}
                    {!canSubmit() && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Cannot Submit Yet
                        </h4>
                        <p className="text-sm text-red-700">
                          Please complete all required verification checks or make appropriate declarations before submitting.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Validation Error Summary */}
            {showValidationErrors && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-700">Please complete all mandatory fields</h4>
                    <p className="text-sm text-red-600 mt-1">The following sections have missing information:</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.keys(validationErrors.basic || {}).length > 0 && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('basic')}>Basic Info</Badge>
                      )}
                      {(Object.keys(validationErrors.address?.present || {}).length > 0 || Object.keys(validationErrors.address?.permanent || {}).length > 0) && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('address')}>Address</Badge>
                      )}
                      {validationErrors.employment?.some(e => Object.keys(e || {}).length > 0) && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('employment')}>Employment</Badge>
                      )}
                      {validationErrors.education?.some(e => Object.keys(e || {}).length > 0) && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('education')}>Education</Badge>
                      )}
                      {Object.keys(validationErrors.idDetails || {}).length > 0 && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('id')}>ID Details</Badge>
                      )}
                      {(validationErrors.personalRef?.some(e => Object.keys(e || {}).length > 0) || validationErrors.professionalRef?.some(e => Object.keys(e || {}).length > 0)) && (
                        <Badge className="bg-red-100 text-red-700 cursor-pointer" onClick={() => setDetailsTab('references')}>References</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSaveDetails}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-lg mt-4"
              data-testid="save-details-button"
            >
              Save All Details
            </Button>
          </div>
        )}

        {activeTab === 'declaration' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Declaration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Candidate Information Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Candidate:</p>
                    <p className="text-slate-800 font-semibold">{employee?.name || user.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Client:</p>
                    <p className="text-slate-800 font-semibold">{clientName || employee?.client_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Verification Agency:</p>
                    <p className="text-slate-800 font-semibold">JustVerified</p>
                  </div>
                </div>
              </div>

              {/* Declaration Content with Contact Card */}
              <div className="flex gap-6">
                {/* Declaration Text */}
                <div className="flex-1 bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="space-y-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {declarationText ? (
                      <p>{declarationText}</p>
                    ) : (
                      <p className="italic text-slate-500">Loading declaration text...</p>
                    )}
                  </div>
                </div>

                {/* Candidate Contact Card */}
                <div className="w-64 flex-shrink-0">
                  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <User className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="font-semibold text-slate-800">{employee?.name || user.name}</p>
                      <p className="text-sm text-slate-500">{employee?.phone || 'N/A'}</p>
                      <p className="text-sm text-slate-500">{employee?.email || user.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {declarationSigned ? (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-emerald-900">Declaration Signed</p>
                    <p className="text-sm text-emerald-700">Signed by: {user.name}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Agreement Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="declaration-agree"
                      checked={declarationAgreed}
                      onChange={(e) => setDeclarationAgreed(e.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      data-testid="declaration-checkbox"
                    />
                    <label htmlFor="declaration-agree" className="text-sm text-slate-700 cursor-pointer">
                      I have read and understood the above declaration. I confirm that all information provided by me is true and accurate to the best of my knowledge. I authorize {clientName || 'the organization'} to conduct background verification as described above.
                    </label>
                  </div>

                  {/* Signature Box */}
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Candidate Signature *</p>
                    <p className="text-xs text-slate-500 mb-3">Please sign in the box below using your mouse or touchscreen</p>
                    <div className="relative">
                      <canvas
                        ref={signatureCanvasRef}
                        width={500}
                        height={150}
                        className="border-2 border-dashed border-slate-300 rounded-lg bg-white w-full cursor-crosshair"
                        style={{ touchAction: 'none' }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        data-testid="signature-canvas"
                      />
                      {!signatureData && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <p className="text-slate-400 text-sm">Sign here</p>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearSignature}
                        className="text-slate-600"
                        data-testid="clear-signature-btn"
                      >
                        Clear Signature
                      </Button>
                    </div>
                  </div>

                  {/* Sign Button */}
                  <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-600">
                        Please check the agreement box and provide your signature to complete the declaration.
                      </p>
                      {!employee?.details_filled && (
                        <p className="text-sm text-amber-600 mt-1">Please fill in your details before signing the declaration.</p>
                      )}
                    </div>
                    <Button
                      onClick={handleSignDeclaration}
                      disabled={!employee?.details_filled || !declarationAgreed || !signatureData}
                      className="bg-slate-900 hover:bg-slate-800 text-white ml-4"
                      data-testid="sign-declaration-button"
                    >
                      Sign Declaration
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Request Dialog */}
      <Dialog open={showEditRequestDialog} onOpenChange={setShowEditRequestDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Request to Edit Basic Information
            </DialogTitle>
            <DialogDescription>
              Select which fields you need to edit and provide a reason. Your request will be reviewed by HR.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold">Fields to Edit *</Label>
              <p className="text-xs text-slate-500 mb-2">Select all fields you need to modify</p>
              <div className="grid grid-cols-2 gap-2">
                {EDITABLE_BASIC_FIELDS.map((field) => (
                  <div 
                    key={field.id} 
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      editRequestFields.includes(field.id) 
                        ? 'bg-amber-50 border-amber-300' 
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                    onClick={() => toggleEditField(field.id)}
                  >
                    <Checkbox 
                      checked={editRequestFields.includes(field.id)}
                      onCheckedChange={() => toggleEditField(field.id)}
                    />
                    <span className="text-sm">{field.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="edit-reason" className="text-sm font-semibold">Reason for Edit *</Label>
              <p className="text-xs text-slate-500 mb-2">Explain why you need to change this information</p>
              <Textarea
                id="edit-reason"
                placeholder="e.g., There was a typo in my name, my phone number has changed..."
                value={editRequestReason}
                onChange={(e) => setEditRequestReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="edit-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditRequestDialog(false);
                setEditRequestFields([]);
                setEditRequestReason('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitEditRequest}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={editRequestFields.length === 0 || !editRequestReason.trim()}
              data-testid="submit-edit-request-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Fill from Resume Dialog */}
      <Dialog open={showAutoFillDialog} onOpenChange={setShowAutoFillDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Review Extracted Data
            </DialogTitle>
            <DialogDescription>
              AI has extracted the following information from your resume. Review and confirm to auto-fill your form.
            </DialogDescription>
          </DialogHeader>
          
          {extractedData && (
            <div className="space-y-4 py-4 text-sm">
              {/* Personal Info */}
              {extractedData.personal_info && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <User className="w-4 h-4" /> Personal Information
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                    {extractedData.personal_info.name && (
                      <p><span className="text-slate-500">Name:</span> {extractedData.personal_info.name}</p>
                    )}
                    {extractedData.personal_info.email && (
                      <p><span className="text-slate-500">Email:</span> {extractedData.personal_info.email}</p>
                    )}
                    {extractedData.personal_info.phone && (
                      <p><span className="text-slate-500">Phone:</span> {extractedData.personal_info.phone}</p>
                    )}
                    {extractedData.personal_info.current_address?.complete_address && (
                      <p><span className="text-slate-500">Address:</span> {extractedData.personal_info.current_address.complete_address}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Employment */}
              {extractedData.employment_history?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Employment History ({extractedData.employment_history.length})
                  </h4>
                  <div className="space-y-2">
                    {extractedData.employment_history.slice(0, 3).map((emp, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3">
                        <p className="font-medium">{emp.company_name}</p>
                        <p className="text-slate-600">{emp.designation}</p>
                        <p className="text-xs text-slate-500">{emp.employed_from} - {emp.employed_to || 'Present'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Education */}
              {extractedData.education?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> Education ({extractedData.education.length})
                  </h4>
                  <div className="space-y-2">
                    {extractedData.education.slice(0, 3).map((edu, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3">
                        <p className="font-medium">{edu.degree || edu.qualification}</p>
                        <p className="text-slate-600">{edu.institution || edu.university}</p>
                        <p className="text-xs text-slate-500">{edu.year_of_passing}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* ID Documents */}
              {extractedData.id_documents && (extractedData.id_documents.pan_number || extractedData.id_documents.aadhar_number) && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> ID Documents
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                    {extractedData.id_documents.pan_number && (
                      <p><span className="text-slate-500">PAN:</span> {extractedData.id_documents.pan_number}</p>
                    )}
                    {extractedData.id_documents.aadhar_number && (
                      <p><span className="text-slate-500">Aadhar:</span> {extractedData.id_documents.aadhar_number}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAutoFillDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={applyExtractedData}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Apply to Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay for resume extraction */}
      {isExtractingResume && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
            <div className="text-center">
              <p className="font-semibold text-slate-900">Extracting data from resume...</p>
              <p className="text-sm text-slate-500">AI is analyzing your document</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
